// Company detail — three-column layout matching DealDetail.
// Left: editable properties.   Center: notes + activity.   Right: engagements + contacts at this company.

// ── Paged notes loading ──────────────────────────────────────────────────────
// The record pages (company / contact / deal) used to fetch a single 100-note
// page and show its length as the count — anything older was silently hidden
// (dozens of companies have 100+ notes; the biggest has 800+). This hook pages
// through /notes: `total` is the server-side count for the scope, `loadMore`
// appends the next page, and `reload` refreshes what's already on screen
// (after a create/edit) without collapsing back to the first page.
const NOTES_PAGE_SIZE = 100;
const NOTES_API_MAX_LIMIT = 500;  // mirrors list_notes' limit cap server-side
const usePagedNotes = (params) => {
  // Callers pass a fresh object literal each render — key on its JSON so the
  // effect only re-runs when the scope actually changes. null = don't fetch.
  const baseKey = JSON.stringify(params || null);
  // Free-text search over the record's notes. Server-side (list_notes `q`,
  // ILIKE on title + body) so it covers every note on the record, not just
  // the pages loaded so far — the ask came from a company with 250+ notes.
  // Both pieces of state remember which record they belong to, so switching
  // records reads as an empty search at once (no stale-term fetch).
  const [searchState, setSearchState] = React.useState({ key: baseKey, text: '' });
  const [qState, setQState] = React.useState({ key: baseKey, q: '' });
  const search = searchState.key === baseKey ? searchState.text : '';
  const q = qState.key === baseKey ? qState.q : '';
  const setSearch = React.useCallback((text) => setSearchState({ key: baseKey, text }), [baseKey]);
  React.useEffect(() => {
    // 250ms debounce — typing shouldn't fire a request per keystroke.
    const t = setTimeout(() => setQState({ key: baseKey, q: search.trim() }), 250);
    return () => clearTimeout(t);
  }, [baseKey, search]);
  const key = React.useMemo(() => {
    const p = JSON.parse(baseKey);
    return p && q ? JSON.stringify({ ...p, q }) : baseKey;
  }, [baseKey, q]);
  const [notes, setNotes] = React.useState(null);
  const [total, setTotal] = React.useState(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const pagesRef = React.useRef(1);
  // Mirror of `notes` so loadMore can merge against the latest list without
  // re-creating its callback on every append.
  const notesRef = React.useRef(null);
  const applyNotes = (items) => { notesRef.current = items; setNotes(items); };

  React.useEffect(() => {
    pagesRef.current = 1;
    applyNotes(null);
    setTotal(null);
    const p = JSON.parse(key);
    if (!p) return undefined;
    let alive = true;
    window.AtlasApi.listNotes({ ...p, page: 1, limit: NOTES_PAGE_SIZE })
      .then((res) => {
        if (!alive) return;
        applyNotes((res && res.items) || []);
        setTotal(res && typeof res.total === 'number' ? res.total : null);
      })
      .catch(() => { if (alive) { applyNotes([]); setTotal(null); } });
    return () => { alive = false; };
  }, [key]);

  const loadMore = React.useCallback(async () => {
    const p = JSON.parse(key);
    if (!p) return;
    const page = pagesRef.current + 1;
    setLoadingMore(true);
    try {
      const res = await window.AtlasApi.listNotes({ ...p, page, limit: NOTES_PAGE_SIZE });
      const items = (res && res.items) || [];
      pagesRef.current = page;
      // Dedupe on append — a note created since page 1 loaded shifts the
      // offset, so the next page can start with rows we already have.
      const prev = notesRef.current || [];
      const seen = new Set(prev.map((n) => n.id));
      const fresh = items.filter((n) => !seen.has(n.id));
      applyNotes([...prev, ...fresh]);
      if (fresh.length === 0) {
        // Dead end — past the last page, or a page that deduped away
        // entirely. Clamp total so the footer hides instead of offering a
        // button that can't load anything (a reload restores the real count).
        setTotal(prev.length);
      } else if (res && typeof res.total === 'number') {
        setTotal(res.total);
      }
    } catch (_) { /* keep what's loaded */ }
    setLoadingMore(false);
  }, [key]);

  const reload = React.useCallback(() => {
    const p = JSON.parse(key);
    if (!p) return;
    // Refresh every loaded page in one call. Past the API's limit cap (5+
    // pages deep) the view trims back to the cap — acceptable for a reload
    // triggered by editing a note near the top.
    const limit = Math.min(NOTES_API_MAX_LIMIT, pagesRef.current * NOTES_PAGE_SIZE);
    pagesRef.current = limit / NOTES_PAGE_SIZE;
    window.AtlasApi.listNotes({ ...p, page: 1, limit })
      .then((res) => {
        applyNotes((res && res.items) || []);
        if (res && typeof res.total === 'number') setTotal(res.total);
      })
      .catch(() => {});
  }, [key]);

  // `search` = live input text; `q` = the debounced term the list reflects.
  return { notes, total, loadingMore, loadMore, reload, search, setSearch, q };
};

// A live deal: not a buyer shell, not exited (`stale` = exit_id set), not
// archived, not in a terminal stage. Mirrors buyer_acquisitions.live_deal_sql
// and the dashboard pipeline — an exited deal left un-archived ("Company
// sold") is finished, not live.
const TERMINAL_STAGES = new Set(['won', 'ss_won', 'buyer_closed']);
const isLiveDeal = (e) =>
  e.type !== 'buyer_buyside' && !e.stale && !e.archived && !TERMINAL_STAGES.has(e.stage);

// ── Phone helpers (the phone company page and the phone Companies list) ─────
// DealCloud phones arrive as free text — "(916) 468-8893    (Work)", an
// extension ("… x204"), two numbers in one field ("… / …"), or junk like
// ".    (Work)". Parsing is window.AtlasPhone.parsePhones (mobile/people.jsx,
// shared with the person page): an extension dials after a pause instead of
// being glued onto the number, and a field holding two numbers is two
// numbers. Returns null when nothing is dialable (no dead tel: link), else
// { shown, href, phones } — `href` dials the first number (outreach.jsx's
// single Call button), `phones` lists every one for a picker.
const phoneDial = (raw) => {
  const AP = window.AtlasPhone;
  if (!AP || !AP.parsePhones) return null;
  const phones = AP.parsePhones(raw);
  if (!phones.length) return null;
  return { shown: phones.map((ph) => ph.display).join(' · '), href: phones[0].tel, phones };
};
// HQ state is stored as the full name (the DealCloud convention, "Texas");
// a list row has room for "Dallas, TX".
const US_STATE_ABBR = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA', Colorado: 'CO',
  Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC', Florida: 'FL', Georgia: 'GA',
  Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL', Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY',
  Louisiana: 'LA', Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND',
  Ohio: 'OH', Oklahoma: 'OK', Oregon: 'OR', Pennsylvania: 'PA', 'Puerto Rico': 'PR', 'Rhode Island': 'RI',
  'South Carolina': 'SC', 'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI', Wyoming: 'WY',
};
const cityStateShort = (city, state) =>
  [city, (state && US_STATE_ABBR[state]) || state].filter(Boolean).join(', ');
// "today" / "3d ago" / "5w ago" / "Mar 2024" — fits a list row's meta column.
const shortAgo = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'today';
  if (days < 14) return `${days}d ago`;
  if (days < 70) return `${Math.floor(days / 7)}w ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const CompanyDetail =({ companyId, activeUser, setView, setDealId, setContactId, back }) => {
  const device = window.useDevice();
  // ?panel= / ?note= for this company (record_panels.jsx); above every early return.
  const panel = window.usePanelParam({ kind: 'company', id: companyId });
  const [company,    setCompany]    = React.useState(null);
  // Phone only reads these (an error card with Retry instead of a spinner
  // that never ends); desktop keeps its toast.
  const [loadError, setLoadError] = React.useState(null);
  const [loadTick, setLoadTick] = React.useState(0);
  const [engagements,setEngagements]= React.useState(null);
  // Deals where THIS company is a buyer (engagements.company_id holds the
  // seller/target side; buyer_engagements holds the buy side).
  const [buyerEngs, setBuyerEngs]   = React.useState(null);
  // The buyer roster row for THIS company, when it has one (GET /buyers/one
  // by company_id; 404 = not a buyer). The buyer page (roadmap item 5) was
  // folded into this one: a firm is one record. `buyer` switches on the
  // buyer header, KPIs, editable investment profile, deals sent, passes and
  // the team/portfolio/acquisitions/signals rail (window.BuyerFirm).
  // undefined = not known yet, null = not a buyer: sections that only a
  // non-buyer gets (seller tools, Facts & fit, Call triggers) wait for it, so
  // a buyer firm never mounts and fetches them only to drop them.
  const [buyer, setBuyer] = React.useState(undefined);
  const [sendOpen, setSendOpen] = React.useState(false);
  const [dealsTick, setDealsTick] = React.useState(0);
  // Buyers interested in THIS company's live seller engagement(s) — the
  // mirror of DealCloud's "Interested Buyer Engagement(s)" table on Company.
  const [interestedBuyers, setInterestedBuyers] = React.useState(null);
  const [contacts,   setContacts]   = React.useState(null);
  const { notes, total: notesTotal, loadingMore: notesLoadingMore,
          loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ } =
    usePagedNotes(companyId ? { company_id: companyId } : null);
  const [users,      setUsers]      = React.useState([]);
  const [industries, setIndustries] = React.useState([]);
  const [followUp,   setFollowUp]   = React.useState(false);
  // Bumped when the Follow up composer closes — its reminder joins Open tasks.
  const [tasksTick,  setTasksTick]  = React.useState(0);
  // "Not acquired" — un-marking asks why (views/acquisition_reversal.jsx).
  const [reversing,  setReversing]  = React.useState(false);
  // Quick-create from the right-rail panels — reuses the People / Deals
  // drawers with this company pre-linked.
  const [addingContact,   setAddingContact]   = React.useState(false);
  const [addingEngagement, setAddingEngagement] = React.useState(false);
  const [stages, setStages] = React.useState([]);

  // Lookups for the editable selects.
  React.useEffect(() => {
    window.AtlasApi.listTeamMembers()
      .then((m) => setUsers(Array.isArray(m) ? m.filter((u) => u.active !== false) : []))
      .catch(() => setUsers([]));
    window.AtlasApi.listIndustries()
      .then((i) => setIndustries(Array.isArray(i) ? i : []))
      .catch(() => setIndustries([]));
    window.AtlasApi.listStages()
      .then((s) => setStages(Array.isArray(s) ? s : []))
      .catch(() => setStages([]));
  }, []);

  // Initial company fetch on id change.
  React.useEffect(() => {
    let alive = true;
    setCompany(null);
    setLoadError(null);
    (async () => {
      try {
        const raw = await window.AtlasApi.getCompany(companyId);
        if (!alive) return;
        setCompany(window.AtlasData.__adaptCompany(raw));
      } catch (e) {
        if (alive) setLoadError(e.message || 'Could not load company');
        pushToast({ type: 'error', title: 'Could not load company', body: e.message });
      }
    })();
    return () => { alive = false; };
  }, [companyId, loadTick]);

  // Related records.
  React.useEffect(() => {
    if (!companyId) return;
    setInterestedBuyers(null);
    window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
      .then((res) => {
        const engs = ((res && res.items) || []).map(window.AtlasData.__adaptEngagement);
        setEngagements(engs);
        // Interested buyers hang off the live seller engagement(s), not the
        // company row — fetch per engagement and pool them (almost always 1).
        const sellers = engs.filter((e) => !e.archived && (e.type === 'seller_buyside' || e.type === 'sell_side'));
        if (!sellers.length) { setInterestedBuyers([]); return; }
        Promise.all(sellers.map((e) =>
          window.AtlasApi.buyersForEngagement(e.id).catch(() => null)
        )).then((rs) => setInterestedBuyers(rs.filter(Boolean).flatMap((r) => r.items || [])));
      })
      .catch(() => { setEngagements([]); setInterestedBuyers([]); });
    setBuyerEngs(null);
    window.AtlasApi.buyerEngagementsForCompany(companyId)
      .then((res) => setBuyerEngs((res && res.items) || []))
      .catch(() => setBuyerEngs([]));
    setBuyer(undefined);
    window.AtlasApi.getBuyer({ company_id: companyId })
      .then((b) => setBuyer(b && b.buyer_id ? b : null))
      .catch(() => setBuyer(null));
    // Reset too: the seller tools read GPP off these people.
    setContacts(null);
    window.AtlasApi.listContacts({ company_id: companyId, limit: 200 })
      .then((res) => setContacts(((res && res.items) || []).map(window.AtlasData.__adaptContact)))
      .catch(() => setContacts([]));
  }, [companyId]);

  // Record-open beacon (api.jsx recordView): once per company, when the
  // roster row and people are in (the variant reads both). Never for a deal guest.
  React.useEffect(() => {
    if (!company || buyer === undefined || contacts === null || window.AtlasIsGuest || !window.AtlasApi.recordView) return;
    window.AtlasApi.recordView({
      page: 'company',
      variant: buyer ? 'buyer_firm' : window.isSellerCompany(company, buyer, contacts) ? 'seller' : 'non_seller',
      device: device.kind, layout: window.recordLayout(), panel: 'none', action: 'open', via: 'load',
    });
  }, [company && buyer !== undefined && contacts !== null ? company.id : null]);  // eslint-disable-line react-hooks/exhaustive-deps

  // What "+ New" (and Buyer type in the New engagement drawer) start from on
  // this page: a buyer firm is the buyer of a buyer engagement; a seller's
  // single live deal is the deal one is bought into.
  const compRoles = (company && company._raw && company._raw.roles) || [];
  const isBuyerFirm = !!buyer || compRoles.includes('buyer') ||
    !!(company && company._raw && ['buyer', 'both'].includes(company._raw.role));
  const liveSellerDeals = (engagements || []).filter((e) => !e.archived && !e.exitName && e.type !== 'buyer_buyside');
  const sellerDealCtx = liveSellerDeals.length === 1 ? { id: liveSellerDeals[0].id, name: liveSellerDeals[0].name } : null;
  React.useEffect(() => {
    if (!company || !window.publishPageContext) return undefined;
    window.publishPageContext({ kind: 'company', id: company.id, name: company.name, isBuyer: isBuyerFirm, sellerDeal: sellerDealCtx });
    return () => window.publishPageContext && window.publishPageContext(null, company.id);
  }, [company && company.id, company && company.name, isBuyerFirm, sellerDealCtx && sellerDealCtx.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveCompany = React.useCallback(async (patch) => {
    try {
      const updated = await window.AtlasApi.updateCompany(companyId, patch);
      setCompany(window.AtlasData.__adaptCompany(updated));
      // An owner change that reached the book (one owner per book): the
      // contacts + deals lists on this page show owners, so re-pull them.
      const c = updated && updated.owner_cascade;
      if (c && (c.contacts || c.engagements)) {
        window.AtlasApi.listContacts({ company_id: companyId, limit: 200 })
          .then((res) => setContacts(((res && res.items) || []).map(window.AtlasData.__adaptContact)))
          .catch(() => {});
        window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
          .then((res) => setEngagements(((res && res.items) || []).map(window.AtlasData.__adaptEngagement)))
          .catch(() => {});
      }
      pushToast({ type: 'success', title: 'Saved' });
      return updated;
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [companyId]);

  // Buyer roster fields (ICP band, agreement, owner…) — PATCH /buyers/one.
  // An ICP edit marks icp_source manual server-side so the site sweep keeps
  // its hands off.
  const saveBuyer = React.useCallback(async (patch) => {
    if (!buyer) return null;
    try {
      const updated = await window.AtlasApi.updateBuyer(buyer.buyer_id, patch);
      setBuyer(updated);
      pushToast({ type: 'success', title: 'Saved' });
      return updated;
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  }, [buyer]);
  // "Add to buyer list": put this firm on the roster by hand (app/buyer_intake).
  // The page then switches to the buyer layout with the profile to fill in.
  const [addingBuyer, setAddingBuyer] = React.useState(false);
  const addToBuyerList = React.useCallback(async () => {
    if (!company || addingBuyer) return;
    setAddingBuyer(true);
    try {
      const res = await window.AtlasApi.addBuyerFirm(company.id);
      if (res && res.buyer) setBuyer(res.buyer);
      window.pushToast && window.pushToast({ type: 'success', title: `${company.name} is on the buyer list` });
    } catch (e) {
      window.pushToast && window.pushToast({ type: 'error', title: 'Could not add to the buyer list', body: e.message });
    } finally {
      setAddingBuyer(false);
    }
  }, [company && company.id, addingBuyer]);  // eslint-disable-line react-hooks/exhaustive-deps
  const refreshBuyer = React.useCallback(() => {
    if (!buyer) return;
    setDealsTick((t) => t + 1);
    window.AtlasApi.getBuyer({ buyer_id: buyer.buyer_id }).then(setBuyer).catch(() => {});
  }, [buyer]);

  // Marking a company acquired closes it out server-side: its live deals exit
  // as "Company sold" and a note records it (update_company →
  // close_out_company). A Won deal is finished and stays. Say so before
  // saving when there is a live deal to exit.
  const toggleAcquired = React.useCallback(async (next) => {
    // Un-marking undoes a close-out (its deals were exited as Company sold), so
    // it reopens them and asks why — the server refuses a bare un-mark.
    if (!next) { setReversing(true); return; }
    const actives = next ? (engagements || []).filter(isLiveDeal) : [];
    if (actives.length) {
      const first = actives[0];
      const ok = await showConfirm({
        title: actives.length === 1 ? 'Mark acquired and exit the deal?' : 'Mark acquired and exit the deals?',
        body: actives.length === 1
          ? `"${first.name}" is still live${first.stageName ? ` at ${first.stageName}` : ''}. Marking the company acquired exits it as "Company sold" and logs a note on the company and the deal.`
          : `This company has ${actives.length} live engagements. Marking it acquired exits them all as "Company sold" and logs a note on the company.`,
        confirmLabel: actives.length === 1 ? 'Mark acquired & exit deal' : 'Mark acquired & exit deals',
        danger: true,
      });
      if (!ok) return;
    }
    const updated = await saveCompany({ is_acquired: next });
    if (!next || !updated) return;
    window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
      .then((res) => setEngagements(((res && res.items) || []).map(window.AtlasData.__adaptEngagement)))
      .catch(() => {});
    reloadNotes();
  }, [engagements, saveCompany, companyId, reloadNotes]);

  // After Confirm / Not it on an acquisition record (the header's "Acquired?"
  // pill, the Ownership panel): a confirm can exit deals and add a note —
  // re-pull all three.
  const afterAcquisitionDecision = React.useCallback(async () => {
    window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
      .then((res) => setEngagements(((res && res.items) || []).map(window.AtlasData.__adaptEngagement)))
      .catch(() => {});
    reloadNotes();
    const fresh = await window.AtlasApi.getCompany(companyId);
    setCompany(window.AtlasData.__adaptCompany(fresh));
  }, [companyId, reloadNotes]);

  // iPad: the three-pane body below needs ≥1140px. Measure the body itself
  // (not the window — the sidebar may be open beside it) and fall back to
  // main + one rail, or one stacked column. The first measurement is taken
  // synchronously in the ref callback and the panes only mount after it, so
  // a wrong first guess never mounts (and fetches) every panel twice. The
  // height sizes the 'two' layout's sticky notes column. Desktop never
  // measures, so it always gets the three panes exactly as before.
  const [paneW, setPaneW] = React.useState(null);
  const [paneH, setPaneH] = React.useState(null);
  const paneRO = React.useRef(null);
  const measureBody = React.useCallback((el) => {
    if (paneRO.current) { paneRO.current.disconnect(); paneRO.current = null; }
    // Desktop: a true no-op (no state writes — even same-value ones cost a
    // render during commit).
    if (!device.tablet) return;
    if (!el) { setPaneW(null); setPaneH(null); return; }
    const take = (w, h) => {
      setPaneW((prev) => (prev === w ? prev : w));
      setPaneH((prev) => (prev === h ? prev : h));
    };
    take(Math.round(el.clientWidth), Math.round(el.clientHeight));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      take(Math.round(r.width), Math.round(r.height));
    });
    ro.observe(el);
    paneRO.current = ro;
  }, [device.tablet]);

  if (!company) {
    if (device.phone) {
      return <CompanyPhoneLoading error={loadError} onRetry={() => setLoadTick((t) => t + 1)} />;
    }
    return <div style={{ padding: 24, fontSize: 12, color: 'var(--text-3)' }}>Loading company…</div>;
  }

  // Acquired with a Won deal = our sale (buyer_acquisitions.won_deal_sql).
  const soldThroughUs = !!company._raw?.is_acquired && (engagements || []).some((e) =>
    e.type !== 'buyer_buyside' && (e.stage === 'won' || e.stage === 'ss_won'));

  const roleOptions = [
    { value: 'seller',  label: 'Seller',  hue: 'teal' },
    { value: 'buyer',   label: 'Buyer',   hue: 'violet' },
    { value: 'both',    label: 'Both',    hue: 'amber' },
    { value: 'partner', label: 'Partner', hue: 'slate' },
    // DC Company.Type "Service Provider" — wealth advisors, accountants,
    // M&A attorneys. Company-side twin of contact_type='professional'.
    { value: 'service_provider', label: 'Service provider', hue: 'blue' },
  ];
  const stateOptions = [
    { value: 'lead_pool', label: 'Lead pool', hue: 'amber' },
    { value: 'active',    label: 'Active',    hue: 'green' },
    { value: 'archived',  label: 'Archived',  hue: 'slate' },
  ];
  const industryOptions = industries.map((i) => ({ value: i.id, label: i.name }));

  const EditableCell = window.EditableCell;
  const BuyerFirm = window.BuyerFirm;
  // Seller brief, comps, data vault and MyExit: sellers only (primitives.jsx).
  // The people list carries GPP (a contact role), so it waits for that too.
  const sellerTools = window.isSellerCompany(company, buyer, contacts);
  // Phone: single-tap flags in the All details sheet confirm first (a scroll that lands
  // on a switch shouldn't mark a company do-not-contact). toggleAcquired confirms
  // when live deals would exit; the plain case gets a simple confirm here.
  const phoneFlagConfirms = {
    onToggleDnc: async (next) => {
      const ok = await window.showConfirm({
        title: next ? `Mark ${company.name} do-not-contact?` : `Clear do-not-contact on ${company.name}?`,
        body: next ? 'It drops out of outreach lists and call queues.' : 'It can be contacted and queued again.',
        confirmLabel: next ? 'Mark DNC' : 'Clear DNC', danger: next,
      });
      if (ok) saveCompany({ is_dnc: next }).catch(() => {});
    },
    // Un-marking opens the "Not acquired" drawer, which asks why — no extra confirm.
    // (liveEngs is declared below; this runs on a tap, long after.)
    onToggleAcquired: async (next) => {
      if (next && !liveEngs.length) {
        const ok = await window.showConfirm({ title: `Mark ${company.name} acquired?`, confirmLabel: 'Mark acquired' });
        if (!ok) return;
      }
      toggleAcquired(next);
    },
  };
  // PanelHost (record_panels.jsx): gate + host for ?panel= on both layouts.
  const panelKnown = buyer !== undefined && contacts !== null;
  const panelFacts = { known: panelKnown, sellerCompany: sellerTools, buyerFirm: !!buyer, companyId };
  const panelVariant = !panelKnown ? 'company' : buyer ? 'buyer_firm' : sellerTools ? 'seller' : 'non_seller';
  const panelCtx = {
    companyId, reloadNotes,
    reloadRecord: () => window.AtlasApi.getCompany(companyId)
      .then((raw) => { if (raw) setCompany(window.AtlasData.__adaptCompany(raw)); }).catch(() => {}),
    company, contacts,
    onCompanyChange: (raw) => setCompany(window.AtlasData.__adaptCompany(raw)),
    onContactCreated: (contact) => {
      setContacts((prev) => [window.AtlasData.__adaptContact(contact), ...(prev || [])]);
      window.dispatchEvent(new CustomEvent('atlas-data-changed'));
    },
    // All details on a phone (CompanyDetailsBody); the v2 page spreads its own on top.
    saveCompany, saveBuyer, onBuyerChange: setBuyer, buyer, users, roleOptions, stateOptions, industryOptions,
    onToggleAcquired: toggleAcquired, onAddToBuyerList: addToBuyerList,
    ...(device.phone ? phoneFlagConfirms : null),
  };
  // KPIs count this company's OWN deals — buy-side bookkeeping shells
  // (buyer_buyside) are excluded; that activity shows under "Buying into".
  const dealEngs     = (engagements || []).filter((e) => e.type !== 'buyer_buyside');
  const liveEngs     = dealEngs.filter(isLiveDeal);
  const archivedEngs = dealEngs.filter((e) => e.archived);
  // Pipeline = live deals only, like the dashboard. Archived engagements are
  // usually reconciled DealCloud duplicates (summing them double-counts), and
  // exited / won deals are no longer pipeline.
  const totalEngFees = liveEngs.reduce((s, e) => s + (e.size || 0), 0);
  const STAGE_DOT_HUE = window.STAGE_DOT_HUE || {};

  // "Not acquired" (views/acquisition_reversal.jsx): un-marking asks why. Built
  // once so the phone layout, which returns early, renders it too.
  const reverseDrawer = window.ReverseAcquisitionDrawer && (
    <window.ReverseAcquisitionDrawer
      open={reversing}
      zIndex={panel.name ? 70 : undefined}
      companyId={companyId}
      companyName={company.name}
      onClose={() => setReversing(false)}
      onDone={async () => {
        setReversing(false);
        // Deals reopened, flag cleared, note filed — re-pull all three.
        window.AtlasApi.listEngagements({ company_id: companyId, limit: 100 })
          .then((res) => setEngagements(((res && res.items) || []).map(window.AtlasData.__adaptEngagement)))
          .catch(() => {});
        reloadNotes();
        try {
          const fresh = await window.AtlasApi.getCompany(companyId);
          setCompany(window.AtlasData.__adaptCompany(fresh));
        } catch (_) {}
      }}
    />
  );

  // Phone: its own page over the same state and save paths (below).
  if (device.phone) {
    return (
      <>
      <CompanyPhone
        companyId={companyId} company={company} activeUser={activeUser}
        engagements={engagements} dealEngs={dealEngs} liveEngs={liveEngs}
        buyerEngs={buyerEngs} buyer={buyer} interestedBuyers={interestedBuyers}
        contacts={contacts} users={users} stages={stages}
        industryOptions={industryOptions}
        notesState={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes,
          reload: reloadNotes, search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
        isBuyerFirm={isBuyerFirm} sellerDealCtx={sellerDealCtx} soldThroughUs={soldThroughUs}
        setDealId={setDealId} setContactId={setContactId}
        onSave={saveCompany} onSaveBuyer={saveBuyer} onBuyerChange={setBuyer}
        onCompanyChange={(raw) => setCompany(window.AtlasData.__adaptCompany(raw))}
        onContactCreated={(contact) => {
          setContacts((prev) => [window.AtlasData.__adaptContact(contact), ...(prev || [])]);
          window.dispatchEvent(new CustomEvent('atlas-data-changed'));
        }}
        onEngagementCreated={(eng) => {
          setEngagements((prev) => [window.AtlasData.__adaptEngagement(eng), ...(prev || [])]);
          window.dispatchEvent(new CustomEvent('atlas-data-changed'));
        }}
        onOwnershipDecided={afterAcquisitionDecision}
        dealsTick={dealsTick} onSendDeal={() => setSendOpen(true)}
        onInterestedRowUpdate={(id, row) => setInterestedBuyers((prev) => (prev || []).map((b) => (b.id === id ? { ...b, ...row } : b)))}
        sendDrawer={buyer && BuyerFirm && (
          <BuyerFirm.SendDealDrawer buyer={buyer} open={sendOpen} onClose={() => setSendOpen(false)} onSent={refreshBuyer} />
        )}
      />
      {reverseDrawer}
      <window.PanelHost kind="company" id={companyId} record={company} facts={panelFacts} variant={panelVariant} ctx={panelCtx} />
      <window.NoteHost kind="company" id={companyId} onChange={reloadNotes} />
      </>
    );
  }

  // Wave D (step 11): the new layout, when the team's record layout is new
  // (Settings → Feature flags; an admin's ⌘K preview; never a phone: the surface is
  // 'phone' there). The v2 page renders RecordPage, whose PanelHost and NoteHost
  // replace the two below; the action drawers
  // (Not acquired, New person, New engagement, Send a deal) are re-mounted by
  // the v2 page from the same state and handlers (views/company_page_v2.jsx).
  if (window.recordSurface() === 'v2' && typeof window.CompanyPageV2 === 'function') {
    return (
      <window.CompanyPageV2 companyId={companyId} company={company} activeUser={activeUser} back={back}
        engagements={engagements} dealEngs={dealEngs} liveEngs={liveEngs} archivedEngs={archivedEngs} totalEngFees={totalEngFees}
        buyerEngs={buyerEngs} buyer={buyer} interestedBuyers={interestedBuyers} contacts={contacts} users={users} stages={stages}
        roleOptions={roleOptions} stateOptions={stateOptions} industryOptions={industryOptions}
        isBuyerFirm={isBuyerFirm} sellerTools={sellerTools} soldThroughUs={soldThroughUs} sellerDealCtx={sellerDealCtx}
        feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes, reload: reloadNotes,
          search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
        onSave={saveCompany} onSaveBuyer={saveBuyer} onBuyerChange={setBuyer} onAddToBuyerList={addToBuyerList} addingBuyer={addingBuyer}
        onToggleAcquired={toggleAcquired} onAcquisitionDecided={afterAcquisitionDecision}
        onCompanyChange={(raw) => setCompany(window.AtlasData.__adaptCompany(raw))}
        onContactCreated={(contact) => { setContacts((prev) => [window.AtlasData.__adaptContact(contact), ...(prev || [])]); window.dispatchEvent(new CustomEvent('atlas-data-changed')); }}
        onEngagementCreated={(eng) => { setEngagements((prev) => [window.AtlasData.__adaptEngagement(eng), ...(prev || [])]); window.dispatchEvent(new CustomEvent('atlas-data-changed')); if (eng?.id && setDealId) setDealId(eng.id); }}
        onInterestedRowUpdate={(id, row) => setInterestedBuyers((prev) => (prev || []).map((b) => (b.id === id ? { ...b, ...row } : b)))}
        dealsTick={dealsTick} onRefreshBuyer={refreshBuyer}
        setDealId={setDealId} setContactId={setContactId}
        panel={{ facts: panelFacts, variant: panelVariant, ctx: panelCtx }}
        shared={{ reverseDrawer }}
        drawers={{ addingContact, setAddingContact, addingEngagement, setAddingEngagement, sendOpen, setSendOpen }} />
    );
  }

  // 'three' = the desktop panes. iPad narrower than they need: 'two' (main +
  // one rail holding both rails' panels) or 'stack' (one scrolling column).
  const paneLayout = !device.tablet || paneW == null || paneW >= 1140 ? 'three' : paneW >= 700 ? 'two' : 'stack';
  // KPI tiles wrap into rows instead of clipping their values. `wrap` keeps
  // the grid even in the iPad's three-pane width (a roster buyer's eight
  // tiles, four per row); desktop is always the flex strip.
  const kpiRowStyle = (cols, wrap) => (paneLayout === 'three' && !(wrap && device.tablet)
    ? { display: 'flex', padding: '14px 16px 0' }
    : { display: 'grid', gridTemplateColumns: `repeat(${paneW < 600 ? 2 : cols}, minmax(0, 1fr))`, rowGap: 12, padding: '14px 16px 0' });

  const railLine = '1px solid var(--border)';
  const paneBodies = {
    // The middle keeps a floor so a narrow window scrolls sideways instead
    // of crushing the notes / deals-sent lists (same as the deal page).
    three: {
      outer:  { flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '360px minmax(420px, 1fr) 360px', gap: 0, overflowX: 'auto' },
      left:   { borderRight: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' },
      center: { overflow: 'auto', padding: 16, background: 'var(--surface-2)' },
      right:  { borderLeft: '1px solid var(--border)', overflow: 'auto', background: 'var(--surface)' },
    },
    // iPad, ~700–1140px of page: notes and the rest of the center in the
    // main column, both rails' panels (right rail first) in one rail beside
    // it. The body scrolls the rail; the center is pinned to the body's
    // height with its own scroll, so the two still scroll independently.
    two: {
      outer:  { flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gridTemplateRows: 'auto 1fr',
                overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
      left:   { gridColumn: 2, gridRow: 2, borderLeft: railLine, borderTop: railLine, background: 'var(--surface)' },
      center: { gridColumn: 1, gridRow: '1 / 3', position: 'sticky', top: 0, alignSelf: 'start', height: paneH || '100%',
                overflow: 'auto', overscrollBehavior: 'contain', padding: 14, background: 'var(--surface-2)', WebkitOverflowScrolling: 'touch' },
      right:  { gridColumn: 2, gridRow: 1, borderLeft: railLine, background: 'var(--surface)' },
    },
    // Narrower still (Split View): one scrolling column — details and
    // contacts, then deals, then the notes.
    stack: {
      outer:  { flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', alignContent: 'start',
                overflowX: 'hidden', overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
      left:   { gridRow: 1, background: 'var(--surface)', borderBottom: railLine },
      center: { gridRow: 3, padding: 14 },
      right:  { gridRow: 2, background: 'var(--surface)', borderBottom: railLine },
    },
  };
  // null = an iPad body not measured yet (render nothing rather than guess).
  const paneBody = device.tablet && paneW == null ? null : paneBodies[paneLayout];

  const leftRail = (
    <>
      {buyer && BuyerFirm && <BuyerFirm.ProfilePanel buyer={buyer} users={users} onSave={saveBuyer} onChange={setBuyer} />}
      <CompanyPropertiesPanel
        company={company}
        buyer={buyer}
        onAddToBuyerList={addToBuyerList}
        users={users}
        roleOptions={roleOptions}
        stateOptions={stateOptions}
        industryOptions={industryOptions}
        onSave={saveCompany}
        onToggleAcquired={toggleAcquired}
        afterCompany={
          <RelatedContactsPanel contacts={contacts} onOpen={(id) => { setContactId && setContactId(id); }}
            onCreate={() => setAddingContact(true)} />
        }
      />
      {(sellerTools || !panelKnown) && window.recordCardsOn('IntelCard') ? null : (
      <SourceScrubPanel
        companyId={companyId}
        company={company}
        contacts={contacts}
        onCompanyChange={(raw) => setCompany(window.AtlasData.__adaptCompany(raw))}
        onContactCreated={(contact) => {
          setContacts((prev) => [window.AtlasData.__adaptContact(contact), ...(prev || [])]);
          window.dispatchEvent(new CustomEvent('atlas-data-changed'));
        }}
      />
      )}
    </>
  );
  const centerPane = (
    <>
      {/* Open tasks on this company — nothing when none are open. */}
      <CompanyTasksCard companyId={companyId} companyName={company.name} activeUser={activeUser} tick={tasksTick} />
      {buyer && BuyerFirm && (
        <>
          <BuyerFirm.DealsPanel buyer={buyer} onOpenDeal={(id) => { setDealId(id); }} tick={dealsTick} onSend={() => setSendOpen(true)} />
          <BuyerFirm.PassesPanel buyer={buyer} />
        </>
      )}
      {/* The seller brief lives on the company (the deal page shows the
          same one) — a prospect with no deal reads it here before a
          call. Sellers only (isSellerCompany): a roster buyer, partner,
          service provider, GPP or referral-only firm has none. */}
      {/* Call triggers (202609301900): owner timing, went dark, nearby sales
          and listings, buyer demand — what to say on the next call. */}
      {/* Facts & fit (202610061500): LinkedIn headcount, Google rating,
          and where the industry rules put this company. */}
      {buyer === null && window.CompanyFactsSection && (
        (sellerTools || !panelKnown) && window.recordCardsOn('IntelCard') ? null : <window.CompanyFactsSection companyId={companyId} sectionId="company.facts" />
      )}
      {buyer === null && window.CompanySignalsSection && (
        <window.CompanySignalsSection companyId={companyId} sectionId="company.signals" marketHere={sellerTools} />
      )}
      {sellerTools && window.SellerBriefSection && (
        <div style={{ marginBottom: 14 }}>
          {window.recordCardsOn('IntelCard') ? <window.IntelCard companyId={companyId} sectionId="company.seller_brief" /> : <window.SellerBriefSection companyId={companyId} sectionId="company.seller_brief" />}
        </div>
      )}
      {sellerTools && window.CompsSection && (
        <div style={{ marginBottom: 14 }}>
          {window.recordCardsOn('MarketCard') ? <window.MarketCard companyId={companyId} sectionId="company.comps" /> : <window.CompsSection companyId={companyId} sectionId="company.comps" />}
        </div>
      )}
      {/* Data vault (202610081200): the CIM working set, filed by folder. */}
      {sellerTools && window.CompanyVaultSection && (
        <div style={{ marginBottom: 14 }}>
          <window.CompanyVaultSection companyId={companyId} sectionId="company.vault" panelRecord={{ kind: 'company', id: companyId }} />
        </div>
      )}
      {/* Cold calls to this company's contacts (PhoneBurner) — renders
          nothing when nobody here was ever called. */}
      <window.DialerCallsPanel companyId={companyId} />
      {window.WebsiteVisitsCard && <window.WebsiteVisitsCard companyId={companyId} variant="card" />}
      <window.RecordNotes kind="company" recordId={companyId}
        feed={{ notes, total: notesTotal, loadingMore: notesLoadingMore, loadMore: loadMoreNotes,
          reload: reloadNotes, search: notesSearch, setSearch: setNotesSearch, q: notesQ }}
        attach={{ company_id: companyId }} activeUser={activeUser} members={users}
        hideCompanyId={companyId} engagementPicker label="Notes"
        taskLinks={{ company: { id: companyId, name: company.name } }}
        onCompanyChange={(raw) => setCompany(window.AtlasData.__adaptCompany(raw))} />
      <div style={{ height: 14 }} />
      <window.AuditTimeline params={{ object_type: 'company', object_id: companyId }} sectionId="company.history" />
      {buyer && (
        <>
          <div style={{ height: 14 }} />
          <window.AuditTimeline params={{ object_type: 'buyer', object_id: buyer.buyer_id }} title="Buyer profile changes" />
        </>
      )}
    </>
  );
  const rightRail = (
    <>
      <RelatedEngagementsPanel engagements={engagements} onOpen={(id) => { setDealId(id); }}
        onCreate={() => setAddingEngagement(true)} />
      {window.recordCardsOn('CompanyBuyersCard') ? (
        <window.CompanyBuyersCard buyers={interestedBuyers} onOpenDeal={(id) => { setDealId(id); }}
          dealNames={Object.fromEntries((engagements || []).map((e) => [e.id, e.name]))}
          onRowUpdate={(id, row) => setInterestedBuyers((prev) => (prev || []).map((b) => (b.id === id ? { ...b, ...row } : b)))} />
      ) : (
      <InterestedBuyersPanel   buyers={interestedBuyers}  onOpenDeal={(id) => { setDealId(id); }}
        dealNames={Object.fromEntries((engagements || []).map((e) => [e.id, e.name]))}
        onRowUpdate={(id, row) => setInterestedBuyers((prev) => (prev || []).map((b) => (b.id === id ? { ...b, ...row } : b)))} />
      )}
      {buyer && BuyerFirm
        ? <BuyerFirm.RailPanels buyer={buyer} setCompanyId={(id) => window.AtlasNav?.goToCompany?.(id)} />
        // Not on the roster but sent a deal anyway — keep the plain list.
        : <BuyerEngagementsPanel buyerEngs={buyerEngs} onOpen={(id) => { setDealId(id); }} />}
      {sellerTools && <MyExitPanel companyId={companyId} contacts={contacts} />}
      <OwnershipPanel companyId={companyId} company={company} buyer={buyer} onDecided={afterAcquisitionDecision} />
      {(sellerTools || !panelKnown) && window.recordCardsOn('MarketCard') ? null : <NearbyAcquisitionsPanel companyId={companyId} />}
      <window.FilesPanel scope={{ company_id: companyId }} title="Files" />
    </>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)' }}>
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px 0', fontSize: 11.5, color: 'var(--text-3)' }}>
          <button onClick={back} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5 }}>
            <Icon.ChevronL size={11} /> Companies
          </button>
          <Icon.ChevronR size={11} color="var(--text-4)" />
          <span>{company.name}</span>
        </div>

        <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <Logo name={company.name} color={company.logo} size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em' }}>{company.name}</h1>
              {/* "Also known as": the name the firm goes by NOW when it differs
                  from the record (a rebrand, an acquisition, a misspelled
                  record). Atlas-only — `name` mirrors DealCloud and is never
                  rewritten for this; edit it in the details pane. */}
              {company._raw?.aka && (
                <span title="Also known as — the name the company goes by now. Edit under Details → Also known as."
                      style={{ fontSize: 15, color: 'var(--text-3)', fontWeight: 500, letterSpacing: '-0.01em' }}>
                  · now <span style={{ color: 'var(--text-2)' }}>{company._raw.aka}</span>
                </span>
              )}
              {/* Derived roles (seller/buyer/referral_partner) — the legacy
                  static `role` enum stays editable in the details pane. */}
              <window.RoleChips roles={company._raw?.roles} details={{
                seller: 'Has a seller-side engagement — see the Engagements panel.',
                buyer: 'Buyer firm — appears in buyer pursuits / the buyer directory.',
                referral_partner: 'Employs an active referral partner (Brand Builder list).',
              }} />
              <Pill hue={company.pipelineState === 'active' ? 'green' : company.pipelineState === 'archived' ? 'slate' : 'amber'} sz="sm">
                {(company.pipelineState || 'lead_pool').replace('_', ' ')}
              </Pill>
              {company._raw?.is_acquired && (soldThroughUs
                ? <Pill hue="green" sz="sm" title="Acquired, and we closed the deal: sold through IndustryPro">sold through us</Pill>
                : <Pill hue="rose" sz="sm">acquired</Pill>)}
              {/* Something says the company was bought and nobody has said yes
                  or no yet (views/acquisition_confirm.jsx). */}
              {window.AcquisitionHeaderPill && (
                <window.AcquisitionHeaderPill companyId={companyId} companyName={company.name}
                  hidden={Boolean(company._raw?.is_acquired)} onDecided={afterAcquisitionDecision} />
              )}
              {company._raw?.is_dnc && <Pill hue="rose" sz="sm">DNC</Pill>}
              {buyer && BuyerFirm && <BuyerFirm.HeaderPills buyer={buyer} />}
              <window.SyncBadge
                lastSyncedAt={company.lastSyncedAt} updatedAt={company.updatedAt}
                dealcloudId={company.dealcloudId || company._raw?.dealcloud_id}
                recordType="company" recordId={companyId}
                onSynced={async () => {
                  const fresh = await window.AtlasApi.getCompany(companyId);
                  setCompany(window.AtlasData.__adaptCompany(fresh));
                }}
              />
              <window.BlastQueueControl companyId={companyId} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 4, fontSize: 12, color: 'var(--text-3)', flexWrap: 'wrap' }}>
              {company.domain && (
                <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer"
                   title={`Open ${company.domain} in a new tab`}
                   style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--accent-text)', textDecoration: 'none' }}
                   onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                   onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                ><Icon.External size={12} /> {company.domain}</a>
              )}
              {company.industry && company.industry !== '—' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Tag size={12} /> {company.industry}</span>}
              {company.hq && company.hq !== '—' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.Building size={12} /> {company.hq}</span>}
              {company.owner && company.owner !== '—' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon.User size={12} /> <UserLink id={company._raw?.assigned_user_id} name={company.owner} /></span>}
              {buyer && buyer.relationship_owner_id && buyer.relationship_owner_id !== company._raw?.assigned_user_id && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }} title="Buyer relationship owner">
                  <Icon.Diamond size={12} /> <UserLink id={buyer.relationship_owner_id} name={buyer.relationship_owner} />
                </span>
              )}
            </div>
            {buyer && buyer.pass_profile && buyer.pass_profile.says_no_to && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
                <b style={{ color: 'var(--text-2)' }}>Says no to:</b> {buyer.pass_profile.says_no_to}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {buyer && BuyerFirm && (
              <Btn variant="primary" size="sm" icon={<Icon.Plus size={12} />} onClick={() => setSendOpen(true)}>Send a deal</Btn>
            )}
            {buyer === null && isBuyerFirm && (
              <Btn variant="primary" size="sm" icon={<Icon.Plus size={12} />} onClick={addToBuyerList} disabled={addingBuyer}>
                {addingBuyer ? 'Adding…' : 'Add to buyer list'}
              </Btn>
            )}
            <Btn size="xs" variant="outline" active={followUp}
                  title={`Create a follow-up reminder for ${company.name}`}
                  onClick={() => setFollowUp(!followUp)}>
              ⏰ Follow up
            </Btn>
          </div>
        </div>

        {followUp && (
          <div style={{ padding: '10px 16px 0', maxWidth: 640 }}>
            <window.FollowUpComposer
              seedTitle={`Follow up on ${company.name}`}
              links={{ company_id: companyId }}
              linkedName={company.name}
              onClose={() => { setFollowUp(false); setTasksTick((t) => t + 1); }} />
          </div>
        )}

        {/* KPIs. A roster buyer gets its own numbers (deals sent, NDAs,
            passes, band…) — a PE firm has no revenue/EBITDA of its own. The
            company row stays underneath only when the firm also has deals of
            its own (role 'both'). */}
        {buyer && BuyerFirm ? (
          <div style={kpiRowStyle(4, true)}>
            <BuyerFirm.Kpis buyer={buyer} contacts={contacts ? contacts.length : '—'} />
          </div>
        ) : null}
        {(!buyer || dealEngs.length > 0) && (
          <div style={kpiRowStyle(3)}>
            <DKpi label="Revenue"     value={fmtMoney(company.revenue)} />
            <DKpi label="EBITDA"      value={fmtMoney(company.ebitda)} />
            <DKpi label="Engagements" value={engagements ? dealEngs.length : '—'} sub={archivedEngs.length > 0 ? `${archivedEngs.length} archived` : null} />
            <DKpi label="Live deals"  value={engagements ? liveEngs.length : '—'} />
            {!buyer && <DKpi label="Contacts"    value={contacts ? contacts.length : '—'} />}
            <DKpi label="Pipeline fees" value={engagements ? fmtMoney(totalEngFees) : '—'} />
          </div>
        )}
        <div style={{ height: 14 }} />
      </div>

      {/* The same three column elements in the same order in every layout —
          only their styles change — so crossing an iPad breakpoint (rotation,
          sidebar) never remounts a panel and drops a half-written note. On
          an iPad the columns mount only once the body is measured. */}
      <div ref={measureBody} style={paneBody ? paneBody.outer : { flex: 1, minHeight: 0 }}>
        {paneBody && (
          <>
            <div style={paneBody.left}>{leftRail}</div>
            <div style={paneBody.center}>{centerPane}</div>
            <div style={paneBody.right}>{rightRail}</div>
          </>
        )}
      </div>
      {buyer && BuyerFirm && (
        <BuyerFirm.SendDealDrawer buyer={buyer} open={sendOpen} onClose={() => setSendOpen(false)} onSent={refreshBuyer} />
      )}
      {reverseDrawer}
      {addingContact && (
        <window.NewPersonDrawer
          initialCompany={{ id: companyId, name: company.name }}
          onClose={() => setAddingContact(false)}
          onCreated={(contact) => {
            setAddingContact(false);
            setContacts((prev) => [window.AtlasData.__adaptContact(contact), ...(prev || [])]);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />
      )}
      {addingEngagement && (
        <window.NewEngagementDrawer
          stages={stages}
          activeUser={activeUser}
          initialCompany={{ id: companyId, name: company.name }}
          initialBuyer={isBuyerFirm ? { id: companyId, name: company.name } : undefined}
          initialDeal={isBuyerFirm ? undefined : (sellerDealCtx || undefined)}
          onClose={() => setAddingEngagement(false)}
          onCreated={(eng) => {
            setAddingEngagement(false);
            setEngagements((prev) => [window.AtlasData.__adaptEngagement(eng), ...(prev || [])]);
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
            // Land on the new deal so the rest of the fields can be filled in.
            if (eng?.id && setDealId) setDealId(eng.id);
          }}
        />
      )}
      <window.PanelHost kind="company" id={companyId} record={company} facts={panelFacts} variant={panelVariant} ctx={panelCtx} />
      <window.NoteHost kind="company" id={companyId} onChange={reloadNotes} />
    </div>
  );
};

// ============ Phone company page ============================================
// CompanyDetail renders this instead of the three panes when
// window.AtlasDevice.phone. Same state and save paths (CompanyDetail owns the
// loads and passes them down); the order follows a call: who they are → call
// / email / log → people → deals → tasks → notes → details, and everything a
// dealmaker rarely needs on the road folds away (and only loads when opened).

const phoneTap = { WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' };

// A rounded group of rows (iOS grouped list). The inner -1px margin hides the
// last row's bottom border under the group's own.
const PhoneGroup = ({ children, style }) => (
  <div style={{ margin: '0 12px', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', background: 'var(--surface)', ...style }}>
    <div style={{ marginBottom: -1 }}>{children}</div>
  </div>
);

// Label · value row for the details list. Module-level on purpose: it is
// also handed to LeadSourceFields as its Row, and a component defined inside
// a render would remount (and drop an open editor) on every save.
const PhoneFieldRow = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '6px 16px', borderBottom: '1px solid var(--border)' }}>
    <span style={{ width: 108, flexShrink: 0, fontSize: 14, color: 'var(--text-3)' }}>{label}</span>
    <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: 'var(--text)', display: 'flex', alignItems: 'center', minHeight: 34, overflowWrap: 'anywhere' }}>
      {value == null || value === '' || value === '—' ? <span style={{ color: 'var(--text-4)' }}>—</span> : value}
    </span>
  </div>
);

// 44px round call / email button on a contact row. A real <a> (not a click
// handler) so iOS — and the native shell — hand tel:/mailto: to the system;
// a button when there are several numbers to choose from (onClick). With
// both, onClick is the link's own handler (the do-not-call check).
const PhoneRoundLink = ({ href, label, icon, external, onClick }) => {
  const live = !!(href || onClick);
  const style = {
    ...phoneTap, width: 44, height: 44, borderRadius: 22, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: live ? 'var(--accent-soft)' : 'var(--surface-2)', color: live ? 'var(--accent-text)' : 'var(--text-4)', textDecoration: 'none',
  };
  if (!href && onClick) return <button type="button" aria-label={label} onClick={onClick} style={{ ...style, border: 'none', cursor: 'pointer' }}>{icon}</button>;
  if (!href) return <span aria-hidden style={style}>{icon}</span>;
  return <a href={href} aria-label={label} onClick={onClick || undefined} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} style={style}>{icon}</a>;
};

// The calling team marked this person do not call → call links ask first
// (window.CallGuard, views/call_button.jsx). undefined = a plain link.
const dncLink = (p, href) => (window.CallGuard && p && p._raw && p._raw.do_not_call
  ? window.CallGuard.linkClick(href, true, 'call', p.name) : undefined);

const PhoneContactRow = ({ p, primary, onOpen, onPickPhone }) => {
  const dial = phoneDial(p.phone);
  const several = !!(dial && dial.phones.length > 1);
  const email = (p.email || '').trim();
  const dm = !!(p._raw && p._raw.is_decision_maker);
  const sub = [p.title && p.title !== '—' ? p.title : null, primary ? 'deal contact' : dm ? 'decision maker' : null, dial && dial.shown]
    .filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: 10, borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
      <button type="button" onClick={() => onOpen(p.id)}
        style={{ ...phoneTap, flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 60, padding: '8px 4px 8px 16px',
                 border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
        <Avatar name={p.name} size={34} color="oklch(0.50 0.13 295)" />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 15.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
          {sub && <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>}
        </span>
      </button>
      <PhoneRoundLink href={dial && !several ? dial.href : null} label={`Call ${p.name}`} icon={<Icon.Phone size={18} />}
        onClick={several ? () => onPickPhone(p) : (dial ? dncLink(p, dial.href) : null)} />
      <PhoneRoundLink href={email && `mailto:${email}`} label={`Email ${p.name}`} icon={<Icon.Mail size={18} />} />
    </div>
  );
};

// One tile of the quick-action row: an <a> when it dials / mails / opens a
// site directly, a button when it opens a picker or the composer.
// linkClick: the <a>'s own onClick (the do-not-call check).
const PhoneQuickAction = ({ label, icon, href, external, onClick, linkClick, disabled }) => {
  const style = {
    ...phoneTap, flex: 1, minWidth: 0, minHeight: 64, borderRadius: 12, border: '1px solid var(--border)',
    background: 'var(--surface)', color: disabled ? 'var(--text-4)' : 'var(--accent-text)', textDecoration: 'none',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5,
    fontSize: 13, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', padding: '8px 4px',
  };
  if (href && !disabled) {
    return <a href={href} onClick={linkClick} style={style} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{icon}<span>{label}</span></a>;
  }
  return <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} style={style}>{icon}<span>{label}</span></button>;
};

const CompanyPhoneLoading = ({ error, onRetry }) => {
  const Sk = window.Skeleton;
  if (error) {
    return (
      <window.PhoneScroll>
        <window.PhoneEmpty title="Couldn't load this company" body={error}
          action={<Btn variant="accent" onClick={onRetry}>Try again</Btn>} />
      </window.PhoneScroll>
    );
  }
  return (
    <window.PhoneScroll>
      <div style={{ padding: 16, background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {Sk && <Sk w={48} h={48} style={{ borderRadius: 10 }} />}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Sk && <Sk w="70%" h={18} />}
            {Sk && <Sk w="45%" h={12} />}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={{ flex: 1 }}>{Sk && <Sk h={64} style={{ borderRadius: 12 }} />}</div>)}
        </div>
      </div>
      <div style={{ padding: 16 }}>{window.SkeletonRows && <window.SkeletonRows rows={5} />}</div>
    </window.PhoneScroll>
  );
};

const CompanyPhone = ({
  companyId, company, activeUser, engagements, dealEngs, liveEngs, buyerEngs, buyer, interestedBuyers,
  contacts, users, stages, industryOptions, notesState, isBuyerFirm, sellerDealCtx,
  soldThroughUs, setDealId, setContactId, onSave, onSaveBuyer, onBuyerChange, onCompanyChange,
  onContactCreated, onEngagementCreated, onOwnershipDecided, dealsTick, onSendDeal, sendDrawer, onInterestedRowUpdate,
}) => {
  const EditableCell = window.EditableCell;
  const BuyerFirm = window.BuyerFirm;
  const raw = company._raw || {};
  const sellerTools = window.isSellerCompany(company, buyer, contacts);
  const [composeRequest, setComposeRequest] = React.useState(null);
  const [picker, setPicker] = React.useState(null);            // 'call' | 'email' | { callId } — who to reach
  // The sheet keeps rendering through its exit animation — draw it from the
  // last picker so it doesn't flip to the full "Call who?" list as it closes.
  const lastPicker = React.useRef(null);
  if (picker) lastPicker.current = picker;
  const [addingContact, setAddingContact] = React.useState(false);
  const [addingEngagement, setAddingEngagement] = React.useState(false);
  const [showAllContacts, setShowAllContacts] = React.useState(false);
  const [showClosed, setShowClosed] = React.useState(false);
  const [tasksTick, setTasksTick] = React.useState(0);          // RecordNotes saved a task → Open tasks re-reads

  // Who "Call" / "Email" reach: the live deal's contact first, then
  // decision-makers, then whoever was in touch last. Companies have no phone
  // of their own — calling the company means calling one of its people.
  const primaryId = (liveEngs[0] && liveEngs[0].contactId) || null;
  const ranked = React.useMemo(() => {
    const score = (p) => (p.id === primaryId ? 4 : 0) + (p._raw && p._raw.is_decision_maker ? 2 : 0);
    return [...(contacts || [])].sort((a, b) => (score(b) - score(a))
      || String(b.lastInteractionAt || '').localeCompare(String(a.lastInteractionAt || '')));
  }, [contacts, primaryId]);
  const callable = ranked.filter((p) => phoneDial(p.phone));
  const emailable = ranked.filter((p) => (p.email || '').trim());

  // Opens RecordNotes' composer (a full-screen sheet on a phone).
  const logNote = () => setComposeRequest({ kind: 'note', n: Date.now() });

  const openPanel = (name, params) => window.openRecordPanel(name, params || {}, { via: 'button' });
  const filesAt = window.RecordPanelsCore.filesPanelFor('company',
    { known: buyer !== undefined && contacts !== null, sellerCompany: sellerTools }, { guest: !!window.AtlasIsGuest });
  const rowLink = (label, onClick, icon) => (
    <button type="button" onClick={onClick}
      style={{ ...phoneTap, display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 52, padding: '8px 16px', border: 'none',
               borderBottom: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' }}>
      {icon && <span style={{ display: 'inline-flex', color: 'var(--text-3)', flexShrink: 0 }}>{icon}</span>}
      <span style={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 600 }}>{label}</span>
      <Icon.ChevronR size={14} color="var(--text-4)" />
    </button>
  );

  const dcFields = raw.dc_synced_fields || [];
  const dcLock = (f) => dcFields.includes(f);
  const sorted = [...(dealEngs || [])].sort((a, b) => (isLiveDeal(b) ? 1 : 0) - (isLiveDeal(a) ? 1 : 0));
  const openDeals = sorted.filter(isLiveDeal);
  const closedDeals = sorted.filter((e) => !isLiveDeal(e));
  const shells = (engagements || []).filter((e) => e.type === 'buyer_buyside');
  const engRow = (e) => (
    <window.PhoneRow key={e.id} onClick={() => setDealId(e.id)} dim={!isLiveDeal(e)}
      title={e.type === 'buyer_buyside' && e.sellerEngagementName ? `Pursuit of ${e.sellerEngagementName}` : e.name}
      subtitle={[
        ENG_TYPE_PILL[e.type] && ENG_TYPE_PILL[e.type].label, e.stageName || e.stage,
        e.onHold && 'on hold', e.exitName && `exited · ${e.exitName}`, e.archived && 'archived',
      ].filter(Boolean).join(' · ')}
      meta={e.size ? fmtMoney(e.size) : null} />
  );
  const contactsShown = showAllContacts ? ranked : ranked.slice(0, 5);
  const location = cityStateShort(raw.hq_city, raw.hq_state);
  const website = raw.website ? (/^https?:\/\//i.test(raw.website) ? raw.website : `https://${raw.website}`) : null;

  return (
    <window.PhoneScroll>
      {/* ── Header ── */}
      <div style={{ padding: '16px 16px 14px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Logo name={company.name} color={company.logo} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ fontSize: 20, fontWeight: 650, color: 'var(--text)', letterSpacing: '-0.015em', lineHeight: 1.25, fontFamily: 'var(--font-display)', overflowWrap: 'anywhere' }}>
              {company.name}
            </h1>
            {raw.aka && <div style={{ fontSize: 14, color: 'var(--text-3)', marginTop: 2 }}>now {raw.aka}</div>}
            <div style={{ fontSize: 14, color: 'var(--text-2)', marginTop: 3 }}>
              {[company.industry !== '—' ? company.industry : null, location].filter(Boolean).join(' · ') || <span style={{ color: 'var(--text-4)' }}>No industry or location</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          <Pill hue={company.pipelineState === 'active' ? 'green' : company.pipelineState === 'archived' ? 'slate' : 'amber'} sz="sm">
            {(company.pipelineState || 'lead_pool').replace('_', ' ')}
          </Pill>
          {raw.is_acquired && (soldThroughUs ? <Pill hue="green" sz="sm">sold through us</Pill> : <Pill hue="rose" sz="sm">acquired</Pill>)}
          {window.AcquisitionHeaderPill && (
            <window.AcquisitionHeaderPill companyId={companyId} companyName={company.name}
              hidden={Boolean(raw.is_acquired)} onDecided={onOwnershipDecided} />
          )}
          {raw.is_dnc && <Pill hue="rose" sz="sm">DNC</Pill>}
          {buyer && BuyerFirm && <BuyerFirm.HeaderPills buyer={buyer} />}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 14, color: 'var(--text-3)' }}>
          <Icon.User size={14} />
          <span>Owner</span>
          <span style={{ color: 'var(--text)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {company.owner && company.owner !== '—' ? company.owner : 'Unassigned'}
          </span>
          {company.team && <span style={{ color: 'var(--text-4)', whiteSpace: 'nowrap' }}>· {company.team}</span>}
        </div>
        {buyer && buyer.relationship_owner && buyer.relationship_owner_id !== raw.assigned_user_id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 14, color: 'var(--text-3)' }}>
            <Icon.Diamond size={14} /> <span>Relationship</span>
            <span style={{ color: 'var(--text)', fontWeight: 500 }}>{buyer.relationship_owner}</span>
          </div>
        )}
        {buyer && buyer.pass_profile && buyer.pass_profile.says_no_to && (
          <div style={{ marginTop: 6, fontSize: 14, color: 'var(--text-3)', lineHeight: 1.45 }}>
            <b style={{ color: 'var(--text-2)' }}>Says no to:</b> {buyer.pass_profile.says_no_to}
          </div>
        )}

        {/* The live deal(s) — one tap to the engagement. */}
        {openDeals.slice(0, 2).map((e) => (
          <button key={e.id} type="button" onClick={() => setDealId(e.id)}
            style={{ ...phoneTap, display: 'flex', alignItems: 'center', gap: 10, width: '100%', marginTop: 12, minHeight: 52, padding: '8px 12px',
                     borderRadius: 12, border: '1px solid var(--border)', background: 'var(--surface-2)', textAlign: 'left', cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
            <span style={{ display: 'inline-flex', color: 'var(--accent)' }}><Icon.Pipeline size={18} /></span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
                <Pill hue="slate" sz="sm">{e.stageName || e.stage}</Pill>
                {e.onHold && <Pill hue="amber" sz="sm">on hold</Pill>}
                {e.size ? <span className="num" style={{ fontSize: 13, color: 'var(--text-3)' }}>{fmtMoney(e.size)}</span> : null}
              </span>
            </span>
            <Icon.ChevronR size={14} color="var(--text-4)" />
          </button>
        ))}
        {openDeals.length > 2 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 6 }}>+{openDeals.length - 2} more live — see Engagements below</div>
        )}

        {/* The numbers that matter at a glance. A roster buyer gets its own. */}
        {/* DKpi tiles carry a right border; the wrapper clips the outer one. */}
        <div style={{ overflow: 'hidden', marginTop: 14, marginLeft: -14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 12, marginRight: -1 }}>
          {buyer && BuyerFirm ? (
            <BuyerFirm.Kpis buyer={buyer} contacts={contacts ? contacts.length : '—'} />
          ) : (
            <>
              <DKpi label="Revenue" value={fmtMoney(company.revenue)} />
              <DKpi label="EBITDA" value={fmtMoney(company.ebitda)} />
              <DKpi label="Live deals" value={engagements ? liveEngs.length : '—'} />
              <DKpi label="Contacts" value={contacts ? contacts.length : '—'} />
            </>
          )}
        </div>
        </div>

        {/* ── Quick actions ── */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <PhoneQuickAction label="Call" icon={<Icon.Phone size={20} />}
            disabled={callable.length === 0}
            href={callable.length === 1 && phoneDial(callable[0].phone).phones.length === 1 ? phoneDial(callable[0].phone).href : null}
            linkClick={callable.length === 1 ? dncLink(callable[0], phoneDial(callable[0].phone).href) : undefined}
            onClick={() => setPicker('call')} />
          <PhoneQuickAction label="Email" icon={<Icon.Mail size={20} />}
            disabled={emailable.length === 0}
            href={emailable.length === 1 ? `mailto:${emailable[0].email.trim()}` : null}
            onClick={() => setPicker('email')} />
          <PhoneQuickAction label="Website" icon={<Icon.Globe size={20} />} href={website} external disabled={!website} />
          <PhoneQuickAction label="Log note" icon={<Icon.Edit size={20} />} disabled={!activeUser}
            onClick={logNote} />
        </div>
        {contacts !== null && callable.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 8 }}>No phone number on file for anyone here.</div>
        )}
        {buyer && BuyerFirm && (
          <Btn variant="accent" icon={<Icon.Plus size={14} />} onClick={onSendDeal} style={{ width: '100%', marginTop: 10, justifyContent: 'center' }}>Send a deal</Btn>
        )}
      </div>

      {/* ── People ── */}
      <window.PhoneSectionHeader title="People" count={contacts ? contacts.length : null}
        action={<Btn size="sm" variant="ghost" icon={<Icon.Plus size={12} />} onClick={() => setAddingContact(true)}>Add</Btn>} />
      <PhoneGroup>
        {contacts === null && <div style={{ padding: 14 }}>{window.SkeletonRows && <window.SkeletonRows rows={3} />}</div>}
        {contacts && contacts.length === 0 && (
          <div style={{ padding: '14px 16px', fontSize: 14, color: 'var(--text-3)' }}>No contacts at this company yet.</div>
        )}
        {contactsShown.map((p) => (
          <PhoneContactRow key={p.id} p={p} primary={p.id === primaryId} onOpen={(id) => setContactId && setContactId(id)}
            onPickPhone={(person) => setPicker({ callId: person.id })} />
        ))}
        {ranked.length > 5 && (
          <button type="button" onClick={() => setShowAllContacts((v) => !v)}
            style={{ ...phoneTap, width: '100%', minHeight: 46, border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--accent-text)', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
            {showAllContacts ? 'Show fewer' : `Show all ${ranked.length}`}
          </button>
        )}
      </PhoneGroup>

      {/* ── Engagements ── */}
      <window.PhoneSectionHeader title="Engagements" count={engagements ? dealEngs.length : null}
        action={<Btn size="sm" variant="ghost" icon={<Icon.Plus size={12} />} onClick={() => setAddingEngagement(true)}>New</Btn>} />
      <PhoneGroup>
        {engagements === null && <div style={{ padding: 14 }}>{window.SkeletonRows && <window.SkeletonRows rows={2} />}</div>}
        {engagements && dealEngs.length === 0 && (
          <div style={{ padding: '14px 16px', fontSize: 14, color: 'var(--text-3)' }}>
            {shells.length ? 'No deals with this company as the client.' : 'No engagements at this company.'}
          </div>
        )}
        {openDeals.map(engRow)}
        {closedDeals.length > 0 && (showClosed ? closedDeals.map(engRow) : (
          <button type="button" onClick={() => setShowClosed(true)}
            style={{ ...phoneTap, width: '100%', minHeight: 46, border: 'none', borderBottom: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--accent-text)', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>
            {`Show ${closedDeals.length} closed or archived`}
          </button>
        ))}
        {/* Folds itself (and hides when no buyer is interested yet). */}
        <InterestedBuyersPanel buyers={interestedBuyers} onOpenDeal={(id) => setDealId(id)} sectionId="phone.company.interested_buyers" onRowUpdate={onInterestedRowUpdate}
          dealNames={Object.fromEntries((engagements || []).map((e) => [e.id, e.name]))} />
        {shells.length > 0 && (
          <window.LazyFold id="phone.company.pursuits" title="Deal pursuits" meta={shells.length} icon={<Icon.Diamond size={18} />}>
            {shells.map(engRow)}
          </window.LazyFold>
        )}
        {!buyer && buyerEngs && buyerEngs.length > 0 && (
          <window.LazyFold id="phone.company.buying_into" title="Buying into" meta={buyerEngs.length} icon={<Icon.Diamond size={18} />}>
            <BuyerEngagementsPanel buyerEngs={buyerEngs} onOpen={(id) => setDealId(id)} />
          </window.LazyFold>
        )}
      </PhoneGroup>

      {/* ── Tasks ── views/record_tasks.jsx */}
      {window.RecordTasks && (
        <window.RecordTasks kind="company" presentation="phone" companyId={companyId} companyName={company.name}
          users={users} activeUser={activeUser} canWrite={!window.AtlasIsGuest} tick={tasksTick} />
      )}

      {/* ── Notes ── the composer opens as a full-screen sheet on a phone. */}
      <div style={{ padding: '18px 12px 0' }}>
        <window.RecordNotes kind="company" recordId={companyId} feed={notesState}
          attach={{ company_id: companyId }} activeUser={activeUser} members={users}
          hideCompanyId={companyId} engagementPicker label="Notes"
          taskLinks={{ company: { id: companyId, name: company.name } }}
          onCompanyChange={onCompanyChange} onTasksChange={() => setTasksTick((t) => t + 1)}
          composeRequest={composeRequest} />
      </div>

      {/* Call triggers, the seller brief and comps fold themselves (phone-only
          fold keys, so a phone never rewrites the desktop's). Each renders
          nothing when it has nothing. All wait until the buyer row is known;
          the seller tools are sellers only (isSellerCompany). */}
      {/* overflowX: the comps header's tab strip is wider than a phone; clip
          it here rather than let it push the whole page sideways. */}
      {buyer === null && (
        <div style={{ padding: '14px 12px 0', overflowX: 'hidden' }}>
          {window.CompanySignalsSection && <window.CompanySignalsSection companyId={companyId} sectionId="phone.company.signals" />}
          {sellerTools && window.SellerBriefSection && (
            <div style={{ marginBottom: 14 }}>
              <window.SellerBriefSection companyId={companyId} sectionId="phone.company.seller_brief" />
            </div>
          )}
          {sellerTools && window.CompsSection && (
            <div style={{ marginBottom: 14 }}>
              <window.CompsSection companyId={companyId} sectionId="phone.company.comps" />
            </div>
          )}
          {sellerTools && window.CompanyVaultSection && (
            <div style={{ marginBottom: 14 }}>
              <window.CompanyVaultSection companyId={companyId} sectionId="phone.company.vault" phone panelRecord={{ kind: 'company', id: companyId }} />
            </div>
          )}
          {window.DialerCallsPanel && <window.DialerCallsPanel companyId={companyId} />}
        </div>
      )}

      {/* ── Details ── tap a value to edit it (pickers open as sheets). */}
      <window.PhoneSectionHeader title="Details" />
      <PhoneGroup>
        <PhoneFieldRow label="Industry" value={<EditableCell type="select" value={company.industryId} options={[{ value: null, label: '— Unassigned —' }, ...industryOptions]} fallbackLabel={raw.industry_name} locked={dcLock('industry_id')} onSave={(industry_id) => onSave({ industry_id })} />} />
        <PhoneFieldRow label="Owner" value={
          <EditableCell type="user" value={raw.assigned_user_id || null} users={users} fallbackLabel={raw.assigned_user_name} locked={dcLock('assigned_user_id')}
            onSave={(assigned_user_id) => window.saveOwnerWithScope({
              kind: 'company', field: 'assigned_user_id', value: assigned_user_id, users,
              companyName: company.name, save: onSave,
            })} />
        } />
        <PhoneFieldRow label="Website" value={<EditableCell type="url" value={raw.website || ''} locked={dcLock('website')} onSave={(w) => onSave({ website: w })} />} />
        <PhoneFieldRow label="HQ city" value={<EditableCell type="text" list={window.GEO && window.GEO.LIST_IDS.city} value={raw.hq_city || ''} locked={dcLock('hq_city')} onSave={(hq_city) => onSave({ hq_city })} />} />
        <PhoneFieldRow label="HQ state" value={<EditableCell type="text" list={window.GEO && window.GEO.LIST_IDS.state} value={raw.hq_state || ''} locked={dcLock('hq_state')} onSave={(hq_state) => onSave({ hq_state })} />} />
        <PhoneFieldRow label="Revenue" value={<EditableCell type="currency" value={company.revenue} locked={dcLock('revenue_est')} onSave={(revenue_est) => onSave({ revenue_est })} />} />
        <PhoneFieldRow label="EBITDA" value={<EditableCell type="currency" value={company.ebitda} locked={dcLock('ebitda_est')} onSave={(ebitda_est) => onSave({ ebitda_est })} />} />
        {/* Everything else (name, role, state, lead source, buyer marketing, milestones, status, ID): ?panel=details. */}
        {rowLink('All details', () => openPanel('details'))}
      </PhoneGroup>

      {/* ── The rest folds away; panels load when opened. "Acquired
          nearby" (the cold-call opener) folds itself and stays one tap away. ── */}
      <window.PhoneSectionHeader title="More" />
      <PhoneGroup>
        {!buyer && <NearbyAcquisitionsPanel companyId={companyId} sectionId="phone.company.nearby_acquisitions" />}
        {buyer && BuyerFirm && (
          <>
            <window.LazyFold id="phone.company.firm_deals" title="Deals with us" icon={<Icon.Pipeline size={18} />}>
              <BuyerFirm.DealsPanel buyer={buyer} onOpenDeal={(id) => setDealId(id)} tick={dealsTick} onSend={onSendDeal} />
            </window.LazyFold>
            <window.LazyFold id="phone.company.firm_passes" title="Passes" icon={<Icon.ThumbDn size={18} />}>
              <BuyerFirm.PassesPanel buyer={buyer} />
            </window.LazyFold>
            <window.LazyFold id="phone.company.firm_profile" title="Investment profile" icon={<Icon.Target size={18} />}>
              <BuyerFirm.ProfilePanel buyer={buyer} users={users} onSave={onSaveBuyer} onChange={onBuyerChange} />
            </window.LazyFold>
            <window.LazyFold id="phone.company.firm_team" title="Team, portfolio & signals" icon={<Icon.Users size={18} />}>
              <BuyerFirm.RailPanels buyer={buyer} setCompanyId={(id) => window.AtlasNav?.goToCompany?.(id)} />
            </window.LazyFold>
          </>
        )}
        <window.LazyFold id="phone.company.ownership" title="Ownership" icon={<Icon.Building size={18} />}>
          <OwnershipPanel companyId={companyId} company={company} buyer={buyer} onDecided={onOwnershipDecided} />
        </window.LazyFold>
        {sellerTools && (
          <window.LazyFold id="phone.company.myexit" title="MyExit assessment" icon={<Icon.ChartBar size={18} />}>
            <MyExitPanel companyId={companyId} contacts={contacts} />
          </window.LazyFold>
        )}
        <window.LazyFold id="phone.company.enrichment" title="Enrichment" icon={<Icon.Sparkle size={18} />}>
          <SourceScrubPanel companyId={companyId} company={company} contacts={contacts}
            onCompanyChange={onCompanyChange} onContactCreated={onContactCreated} />
        </window.LazyFold>
        {/* Files: the vault's All attachments (seller), else the Files workspace (firm, non-seller). */}
        {filesAt && rowLink('Files', () => openPanel(filesAt.name, filesAt.params), <Icon.Folder size={18} />)}
        <window.LazyFold id="phone.company.history" title="Change history" icon={<Icon.Clock size={18} />}>
          {window.AuditTimeline && <window.AuditTimeline params={{ object_type: 'company', object_id: companyId }} />}
        </window.LazyFold>
      </PhoneGroup>
      <div style={{ height: 'calc(24px + var(--sab, 0px))' }} />

      {/* Who to call / email when there's more than one choice — one row per
          number, so a person with two numbers (or an extension) is two rows.
          { callId } = just that person's numbers (their row's Call button). */}
      {(() => {
        const shown = picker || lastPicker.current;
        const one = shown && shown.callId ? callable.find((p) => p.id === shown.callId) : null;
        const rows = shown === 'email'
          ? emailable.map((p) => ({ key: p.id, p, href: `mailto:${p.email.trim()}`, sub: p.email }))
          : (one ? [one] : callable).flatMap((p) => ((phoneDial(p.phone) || {}).phones || []).map((ph) => ({
            key: `${p.id}|${ph.tel}`, p, href: ph.tel, sub: [ph.display, ph.label].filter(Boolean).join(' · '),
          })));
        const title = shown === 'email' ? 'Email who?' : one ? `Call ${one.name}` : 'Call who?';
        return (
          <window.PhoneSheet open={!!picker} onClose={() => setPicker(null)} title={title}>
            {rows.map(({ key, p, href, sub }) => (
              <a key={key} href={href}
                onClick={(e) => { setPicker(null); const ask = shown !== 'email' && dncLink(p, href); if (ask) ask(e); }}
                style={{ ...phoneTap, display: 'flex', alignItems: 'center', gap: 12, minHeight: 60, padding: '8px 18px', borderTop: '1px solid var(--border)', textDecoration: 'none', color: 'var(--text)' }}>
                <Avatar name={p.name} size={34} color="oklch(0.50 0.13 295)" />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 16, fontWeight: 500 }}>{p.name}</span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[!one && p.title && p.title !== '—' ? p.title : null, sub].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span style={{ color: 'var(--accent-text)', display: 'inline-flex' }}>{picker === 'email' ? <Icon.Mail size={18} /> : <Icon.Phone size={18} />}</span>
              </a>
            ))}
          </window.PhoneSheet>
        );
      })()}

      {sendDrawer}
      {addingContact && (
        <window.NewPersonDrawer
          initialCompany={{ id: companyId, name: company.name }}
          onClose={() => setAddingContact(false)}
          onCreated={(contact) => { setAddingContact(false); onContactCreated(contact); }}
        />
      )}
      {addingEngagement && (
        <window.NewEngagementDrawer
          stages={stages}
          activeUser={activeUser}
          initialCompany={{ id: companyId, name: company.name }}
          initialBuyer={isBuyerFirm ? { id: companyId, name: company.name } : undefined}
          initialDeal={isBuyerFirm ? undefined : (sellerDealCtx || undefined)}
          onClose={() => setAddingEngagement(false)}
          onCreated={(eng) => {
            setAddingEngagement(false);
            onEngagementCreated(eng);
            if (eng?.id && setDealId) setDealId(eng.id);
          }}
        />
      )}
    </window.PhoneScroll>
  );
};

// ============ Open tasks card (center column) ===============================
// The company's open tasks in the deal page's Tasks panel (window.TasksPanel,
// deal_detail.jsx), handed a "deal" with no engagement — so its Add files the
// task on the company alone (POST /tasks takes a null engagement_id). Renders
// nothing while loading or when nothing is open; `showEmpty` (the v2 page, for
// a writer) keeps it at 0 open with a dimmed — row — not when the read failed
// (a failed read isn't 0). `openTitle` (the v2 page) heads it Open tasks · N.
// `tick` re-reads it (the header's Follow up composer closed); so does any
// 'task-created' event.
const CompanyTasksCard = ({ companyId, companyName, activeUser, tick, showEmpty = false, openTitle = false }) => {
  const [tasks, setTasks] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const [reloadTick, setReloadTick] = React.useState(0);
  // A different company starts empty; a re-read keeps the rows on screen so
  // a just-checked task's "Done" linger isn't unmounted mid-way.
  React.useEffect(() => { setTasks(null); }, [companyId]);
  React.useEffect(() => {
    if (!companyId) return undefined;
    let alive = true;
    window.AtlasApi.listTasks({ company_id: companyId, status: 'open', limit: 50 })
      .then((res) => { if (alive) { setTasks((res && res.items) || []); setFailed(false); } })
      .catch(() => { if (alive) { setTasks([]); setFailed(true); } });
    return () => { alive = false; };
  }, [companyId, tick, reloadTick]);
  React.useEffect(() => {
    const onCreated = () => setReloadTick((t) => t + 1);
    window.addEventListener('task-created', onCreated);
    return () => window.removeEventListener('task-created', onCreated);
  }, []);
  const reload = React.useCallback(() => setReloadTick((t) => t + 1), []);
  const scope = React.useMemo(() => ({ id: null, target: companyId, name: companyName }), [companyId, companyName]);

  const TasksPanel = window.TasksPanel;
  if (!TasksPanel || !tasks || (tasks.length === 0 && !(showEmpty && !failed))) return null;
  return (
    <div style={{ marginBottom: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {/* -1px: the last row's bottom border sits under the card's own. */}
      <div style={{ marginBottom: -1 }}>
        <TasksPanel deal={scope} activeUser={activeUser} tasks={tasks} onChange={reload} dimEmpty={showEmpty} openTitle={openTitle} />
      </div>
    </div>
  );
};

// ============ KPI tile ============
const DKpi = ({ label, value, sub }) => (
  <div style={{ flex: 1, padding: '0 14px 12px', borderRight: '1px solid var(--border)', minWidth: 0 }}>
    <div style={{ fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
      <span style={{ fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)', letterSpacing: '-0.015em', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      {sub && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{sub}</span>}
    </div>
  </div>
);

// ============ AI-fill provenance badge ============
// The sparkle next to a field says "a web search / model wrote this" — the
// research wand's applied badge (window.ResearchSparkle); a click opens how
// in the research drawer (?panel=research&src=fill): when, the model's
// answer and the pages it cited (enrichment_data.fills[key]).
const AiFillBadge = ({ fill, label, fillKey }) => (
  <window.ResearchSparkle
    onClick={(e) => { e.stopPropagation(); if (fillKey && window.openResearchRun) window.openResearchRun({ src: 'fill', field: fillKey }); }}
    title={`Found by ${fill.fill_mode === 'web_search' ? 'web search' : 'AI'} — click for the sources and how it got here`}
    label={`${label}: how it was filled`} />
);

// ============ Left rail: company properties ============
// All details on a phone (?panel=details, a full-screen sheet): finger-sized rows.
const rowPhone = () => !!(window.AtlasDevice && window.AtlasDevice.phone);
// One row of the properties panel below. Module scope so its identity is
// stable across the panel's renders; the panel passes its AI-fill map
// (`fills`, read with `fillKey`) and research controller (`rs`, read with
// `research`).
const Row = ({ label, value, fillKey, research, fills, rs }) => {
  const fill = fillKey ? fills[fillKey] : null;
  const legacy = fill ? <AiFillBadge fill={fill} label={label} fillKey={fillKey} /> : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: rowPhone() ? '0 0 0 16px' : '6px 14px', minHeight: rowPhone() ? 48 : 30 }}>
      <span style={{ width: 110, fontSize: rowPhone() ? 14 : 11.5, color: 'var(--text-3)', flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, fontSize: rowPhone() ? 15 : 12, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis',
        display: 'inline-flex', alignItems: 'center', gap: 5, ...(rowPhone() ? { alignSelf: 'stretch', minHeight: 48, paddingRight: 16 } : null) }}>
        {value == null || value === '' || value === '—'
          ? <span style={{ color: 'var(--text-4)' }}>—</span>
          : value}
        {research
          ? <window.ResearchField ctl={rs} label={label} legacy={legacy} {...research} />
          : legacy}
      </span>
    </div>
  );
};

// `afterCompany` slots a block (the Contacts list) between the Company and
// Lead source groups. `firm` (the v2 All details on a roster buyer firm, under
// its investment profile): Company / Location / Status / ID only — no Business,
// Lead source, Financials, Buyer marketing or Milestones (the seller groups).
const CompanyPropertiesPanel = ({ company, buyer, onAddToBuyerList, users, roleOptions, stateOptions, industryOptions, onSave, onToggleAcquired, onToggleDnc, afterCompany, firm = false }) => {
  const EditableCell = window.EditableCell;
  // Field-level DealCloud provenance (CompanyRead.dc_synced_fields). EditableCell
  // no longer gates on it (the "edit anyway?" confirm is gone, 2026-10-20), so
  // `locked` here is informational only.
  const dcFields = (company._raw && company._raw.dc_synced_fields) || [];
  const dcLock = (f) => dcFields.includes(f);
  // AI-fill provenance (enrichment_data.fills, written by the field-fill
  // system and the geo enrichment): the research sparkle marks values a web
  // search / model produced; clicking it opens the research drawer on that fill.
  const fills = (company._raw && company._raw.enrichment_data && company._raw.enrichment_data.fills) || {};
  // Research wand (views/research.jsx): `research` = { field, kind, value,
  // onApply } puts the wand after the value, and the ✨ badge once a
  // researched value is in use. The older fill badge shows when research
  // never touched the field.
  const rs = window.useFieldResearch('company', company.id);

  // Long text with the label above it (descriptors, description, services):
  // the wand sits next to the label. `write` = drafted in the house format.
  const TextBlock = ({ label, field, write = false, placeholder, last = false }) => (
    <div style={{ padding: last ? '2px 14px 10px' : '2px 14px 8px' }}>
      {/* Phone: the label (and its wand) is a 44px line, so the text's own tap box below never covers the wand. */}
      <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5,
        ...(rowPhone() ? { minHeight: 44, marginBottom: 0, fontSize: 14 } : null) }}>
        <span>{label}</span>
        <window.ResearchField ctl={rs} field={field} label={label} kind="longtext" write={write}
          value={company._raw?.[field]} onApply={(v) => onSave({ [field]: v })} />
      </div>
      <div style={{ fontSize: rowPhone() ? 15 : 12, color: 'var(--text)' }}>
        <EditableCell type="multiline" label={label} value={company._raw?.[field] || ''} placeholder={placeholder}
          locked={dcLock(field)} onSave={(v) => onSave({ [field]: v || null })} />
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ padding: '12px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</div>
      <Row label="Name"     value={<EditableCell type="text"   value={company.name}                          locked={dcLock('name')} onSave={(name) => onSave({ name })} />} />
      <Row label="Also known as" fillKey="aka" fills={fills} value={<EditableCell type="text" value={company._raw?.aka || ''} placeholder="the name they go by now" onSave={(aka) => onSave({ aka: aka || null })} />} />
      <Row label="Website" fillKey="website" fills={fills} rs={rs} research={{ field: 'website', kind: 'url', value: company._raw?.website, onApply: (website) => onSave({ website }) }} value={<EditableCell type="url"    value={company._raw?.website || ''}           locked={dcLock('website')} onSave={(website) => onSave({ website })} />} />
      <Row label="Role"     value={<EditableCell type="select" value={company._raw?.role}     options={roleOptions}     locked={dcLock('role')} onSave={(role) => onSave({ role })} />} />
      <Row label="State"    value={<EditableCell type="select" value={company.pipelineState}  options={stateOptions}    onSave={(pipeline_state) => onSave({ pipeline_state })} />} />
      {/* industryOptions is active-only and `users` is filtered to active
          members, so both of these routinely hold an id the list no longer
          carries (a collapsed industry, a departed dealmaker) — fallbackLabel
          hands the cell the server-resolved name to show instead of the id. */}
      <Row label="Industry" rs={rs} research={{ field: 'industry_id', kind: 'industry', value: company.industryId, display: company._raw?.industry_name, onApply: (industry_id) => onSave({ industry_id }) }} value={<EditableCell type="select" value={company.industryId}     options={[{ value: null, label: '— Unassigned —' }, ...industryOptions]} fallbackLabel={company._raw?.industry_name} locked={dcLock('industry_id')} onSave={(industry_id) => onSave({ industry_id })} />} />
      <Row label="Owner"    value={
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <EditableCell type="user"   value={company._raw?.assigned_user_id || null} users={users} fallbackLabel={company._raw?.assigned_user_name} locked={dcLock('assigned_user_id')}
            onSave={(assigned_user_id) => window.saveOwnerWithScope({
              kind: 'company', field: 'assigned_user_id', value: assigned_user_id, users,
              companyName: company.name, save: onSave,
            })} />
          {company._raw?.owner_locked && <window.OwnerLockHint kind="company" />}
        </span>
      } />

      {/* Atlas-only profile text (202610061000): what the company does and
          what it sells. Not DealCloud fields, so never locked. */}
      {!firm && (<>
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Business</div>
      <TextBlock label="Business description" field="business_description" placeholder="what the company does, for whom, where" />
      <TextBlock label="Services" field="services" placeholder="one service or product per line" last />
      </>)}

      {afterCompany}

      {/* Lead source (142): where this company FIRST came from — the channel,
          the specific event, the lead's own date. Set once; its deals inherit. */}
      {!firm && (<>
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Lead source</div>
      <window.LeadSourceFields raw={company._raw || {}} onSave={onSave} Row={Row} withDate withDetail />
      </>)}

      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Location</div>
      <Row label="HQ city" fillKey="hq_address" fills={fills} rs={rs} research={{ field: 'hq_city', value: company._raw?.hq_city, onApply: (hq_city) => onSave({ hq_city }) }} value={<EditableCell type="text" list={window.GEO.LIST_IDS.city}    value={company._raw?.hq_city  || ''} locked={dcLock('hq_city')} onSave={(hq_city)  => onSave({ hq_city })} />} />
      <Row label="HQ state" fillKey="hq_address" fills={fills} rs={rs} research={{ field: 'hq_state', value: company._raw?.hq_state, onApply: (hq_state) => onSave({ hq_state }) }} value={<EditableCell type="text" list={window.GEO.LIST_IDS.state}   value={company._raw?.hq_state || ''} locked={dcLock('hq_state')} onSave={(hq_state) => onSave({ hq_state })} />} />
      <Row label="Country"  value={<EditableCell type="text" list={window.GEO.LIST_IDS.country} value={company._raw?.country  || ''} locked={dcLock('country')} onSave={(country)  => onSave({ country })} />} />

      {!firm && (<>
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Financials</div>
      <Row label="Revenue" fillKey="revenue_est" fills={fills} rs={rs} research={{ field: 'revenue_est', kind: 'money', value: company.revenue, onApply: (revenue_est) => onSave({ revenue_est }) }} value={<EditableCell type="currency" value={company.revenue} locked={dcLock('revenue_est')} onSave={(revenue_est) => onSave({ revenue_est })} />} />
      <Row label="EBITDA" fillKey="ebitda_est" fills={fills} rs={rs} research={{ field: 'ebitda_est', kind: 'money', value: company.ebitda, onApply: (ebitda_est) => onSave({ ebitda_est }) }} value={<EditableCell type="currency" value={company.ebitda}  locked={dcLock('ebitda_est')} onSave={(ebitda_est)  => onSave({ ebitda_est })}  />} />

      {/* Pre-NDA buyer marketing text (DC "Blind Descriptor" / "PB Descriptor"
          / "Unblinded Notes"), ordered blind teaser → full blurb — what gets
          shared with buyers before an NDA. Long text, so these get
          label-above blocks instead of Rows. */}
      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Buyer marketing</div>
      {/* The wands here WRITE rather than look up: a draft in the house
          format (app/field_research.py WRITE_FORMATS), from the notes. */}
      <TextBlock label="Blind descriptor" field="blind_descriptor" write />
      <TextBlock label="PB/PEG descriptor" field="pb_descriptor" write />
      <TextBlock label="Unblinded notes" field="unblinded_notes" write last />

      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Milestones</div>
      <Row label="Tracker" value={<window.TrackerStageCell company={company} />} />
      <div style={{ padding: '4px 14px 10px' }}>
        <window.SignalsChips signals={company.signals || {}} />
      </div>
      </>)}

      <div style={{ padding: '14px 14px 6px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', borderTop: '1px solid var(--border)' }}>Status</div>
      <Row label="Acquired" value={
        <button onClick={() => (onToggleAcquired || ((v) => onSave({ is_acquired: v })))(!company._raw?.is_acquired)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--accent-text)', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2, ...(window.phoneHitHost ? window.phoneHitHost() : null),
        }}>{window.PhoneHit && <window.PhoneHit />}{company._raw?.is_acquired ? 'Yes — un-mark' : 'No — mark acquired'}</button>
      } />
      <Row label="DNC" value={
        <button onClick={() => (onToggleDnc || ((v) => onSave({ is_dnc: v })))(!company._raw?.is_dnc)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
          color: 'var(--accent-text)', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2, ...(window.phoneHitHost ? window.phoneHitHost() : null),
        }}>{window.PhoneHit && <window.PhoneHit />}{company._raw?.is_dnc ? 'Yes — un-mark' : 'No — mark do-not-contact'}</button>
      } />
      {buyer === null && onAddToBuyerList && (
        <Row label="Buyer list" value={
          <button onClick={onAddToBuyerList} style={{
            background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
            color: 'var(--accent-text)', fontSize: 12, textDecoration: 'underline', textUnderlineOffset: 2, ...(window.phoneHitHost ? window.phoneHitHost() : null),
          }}>{window.PhoneHit && <window.PhoneHit />}No — add to buyer list</button>
        } />
      )}

      <Row label="ID" value={<window.RecordId value={company._raw?.dealcloud_id} />} />
    </div>
  );
};

// ============ Note feed helpers (shared via window) ============

// Small toggle chip used for the per-subtype note filter. Kept local — if
// other panels start needing this, lift it into primitives.jsx.
const FilterChip = ({ active, label, count, onClick }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 9px', height: 22, borderRadius: 5,
    background: active ? '#fff' : 'transparent',
    border: active ? '1px solid var(--border)' : '1px solid transparent',
    boxShadow: active ? 'var(--shadow-sm)' : 'none',
    fontSize: 11.5, fontWeight: active ? 600 : 500,
    color: active ? 'var(--text)' : 'var(--text-3)',
    cursor: 'pointer', whiteSpace: 'nowrap', ...(window.phoneHitHost ? window.phoneHitHost() : null),
  }}>
    {window.PhoneHit && <window.PhoneHit />}
    {label}
    {count != null && (
      <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>
        {count}
      </span>
    )}
  </button>
);

// ── Shared note facets + filter chips + email-thread grouping ───────────────
// Defined here (where FilterChip/Pill/Markdown/HUE are in scope) and attached
// to window so the one record feed (RecordNotes, views/record_notes.jsx)
// reuses the same chips + thread rendering on every record page.

// note_type → short chip label.
const NOTE_TYPE_LABELS = {
  email: 'Email', call: 'Call', meeting: 'Meeting',
  memo: 'Memo', ai_summary: 'Summary', internal: 'Internal', general: 'Note',
};
// Type chip display order (most useful first); unknown types sort after.
const NOTE_TYPE_ORDER = ['email', 'call', 'meeting', 'memo', 'ai_summary', 'internal', 'general'];

// Available filter facets for a notes list: types (by note_type) + subtypes
// (by DealCloud-vocabulary dc_note_subtype).
const noteFacets = (notes) => {
  const types = {}, subs = {};
  // One meeting can arrive as several notes (invite + transcript + recap) —
  // count each meeting once so the Meeting chip reflects meetings, not notes.
  const mtgKeys = meetingNoteKeys(notes);
  const seenMeetings = new Set();
  for (const n of (notes || [])) {
    if (n.note_type) {
      const mk = n.note_type === 'meeting' ? mtgKeys.get(n.id) : null;
      if (!mk || !seenMeetings.has(mk)) {
        if (mk) seenMeetings.add(mk);
        types[n.note_type] = (types[n.note_type] || 0) + 1;
      }
    }
    const s = n.dc_note_subtype;
    if (s && s !== 'Other') subs[s] = (subs[s] || 0) + 1;  // 'Other' = DC catch-all
  }
  const typeList = Object.keys(types).sort((a, b) => {
    const ia = NOTE_TYPE_ORDER.indexOf(a), ib = NOTE_TYPE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  }).map((v) => ({ value: v, label: NOTE_TYPE_LABELS[v] || v, count: types[v] }));
  const subList = Object.keys(subs).sort((a, b) => subs[b] - subs[a])
    .map((v) => ({ value: v, count: subs[v] }));
  return { types: typeList, subtypes: subList };
};

// Two-dimensional filter (note_type AND dc_note_subtype). Either may be null.
const filterNotes = (notes, typeFilter, subtypeFilter) => {
  if (!notes) return notes;
  if (!typeFilter && !subtypeFilter) return notes;
  return notes.filter((n) =>
    (!typeFilter || n.note_type === typeFilter) &&
    (!subtypeFilter || n.dc_note_subtype === subtypeFilter));
};

// Chip row: All + a chip per note_type + a chip per dc_note_subtype. Only one
// dimension is active at a time (picking a type clears the subtype, etc.) to
// keep the model simple. Hidden when there's nothing meaningful to filter.
const NoteFilterChips = ({ notes, typeFilter, subtypeFilter, onType, onSubtype }) => {
  const { types, subtypes } = React.useMemo(() => noteFacets(notes), [notes]);
  if (!notes || (types.length <= 1 && subtypes.length === 0)) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center',
      padding: '8px 14px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)',
    }}>
      <FilterChip active={!typeFilter && !subtypeFilter} label="All" count={notes.length}
        onClick={() => { onType(null); onSubtype(null); }} />
      {types.map((t) => (
        <FilterChip key={'t:' + t.value} active={typeFilter === t.value} label={t.label} count={t.count}
          onClick={() => { onSubtype(null); onType(typeFilter === t.value ? null : t.value); }} />
      ))}
      {subtypes.length > 0 && types.length > 0 && (
        <span style={{ width: 1, height: 14, background: 'var(--border)', margin: '0 2px' }} />
      )}
      {subtypes.map((s) => (
        <FilterChip key={'s:' + s.value} active={subtypeFilter === s.value} label={s.value} count={s.count}
          onClick={() => { onType(null); onSubtype(subtypeFilter === s.value ? null : s.value); }} />
      ))}
    </div>
  );
};

// A synced email carries its Graph conversation id in metadata. Prefer the
// mailbox-independent conversation_key (Graph conversationIds are per-mailbox,
// so the same thread synced through two users' credentials has two different
// ids — grouping on the raw id split one thread into two cards).
const noteConversationId = (n) =>
  (n && n.note_type === 'email' && n.metadata &&
    (n.metadata.conversation_key || n.metadata.conversation_id)) || null;

// Fold emails sharing a conversation_id (>=2) into one thread item, positioned
// at the newest message (notes arrive newest-first). Everything else passes
// through as a plain note. Returns ordered display items.
const groupEmailThreads = (notes) => {
  if (!notes) return [];
  const byConv = {};
  for (const n of notes) {
    const c = noteConversationId(n);
    if (c) (byConv[c] = byConv[c] || []).push(n);
  }
  const items = [], emitted = new Set();
  for (const n of notes) {
    const c = noteConversationId(n);
    if (c && byConv[c].length >= 2) {
      if (emitted.has(c)) continue;
      emitted.add(c);
      const emails = byConv[c];  // newest-first (input order)
      items.push({ kind: 'thread', conversationId: c, emails, latest: emails[0], count: emails.length });
    } else {
      items.push({ kind: 'note', note: n });
    }
  }
  return items;
};

// ── Meeting-note grouping ────────────────────────────────────────────────────
// One real meeting can produce up to three notes: the Outlook calendar event,
// the Fireflies transcript, and a hand-written recap. Fold them into a single
// feed card (and count them as one meeting) via a shared meeting key.

// Local calendar day a note belongs to: the explicit call_date when set, else
// the local day of dc_created_at (event start for synced meetings) / created_at.
const _noteDay = (n) => {
  if (n.call_date) return String(n.call_date).slice(0, 10);
  const raw = n.dc_created_at || n.created_at;
  const d = raw && new Date(raw);
  if (!d || isNaN(d)) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// note id → meeting key, for every meeting note that can be tied to a meeting:
//   outlook_calendar — metadata.ical_uid (Graph iCalUId)
//   fireflies        — metadata.calendar_note_id / calendar_ical_uid, stamped
//                      at sync time by the same matcher the Calendar view uses
//   manual           — metadata.related_note_id (Calendar-drawer composer);
//                      a recap typed on a record page carries no linkage, so it
//                      folds into the day's synced meeting when that day has
//                      exactly one (two same-day meetings stay unfolded).
const meetingNoteKeys = (notes) => {
  const keys = new Map();
  const synced = [], manual = [];
  for (const n of (notes || [])) {
    if (n && n.note_type === 'meeting') (n.external_source ? synced : manual).push(n);
  }
  // Calendar notes first so a transcript's calendar_note_id resolves to
  // whatever key its calendar note got (uid when present, note id when not).
  for (const n of synced) {
    if (n.external_source === 'fireflies') continue;
    keys.set(n.id, ((n.metadata || {}).ical_uid) || 'note:' + n.id);
  }
  for (const n of synced) {
    if (n.external_source !== 'fireflies') continue;
    const md = n.metadata || {};
    keys.set(n.id, (md.calendar_note_id && keys.get(md.calendar_note_id))
      || md.calendar_ical_uid
      || 'note:' + (md.calendar_note_id || n.id));
  }
  const dayKeys = new Map();  // day → distinct synced meeting keys that day
  for (const n of synced) {
    const day = _noteDay(n);
    if (!day) continue;
    if (!dayKeys.has(day)) dayKeys.set(day, new Set());
    dayKeys.get(day).add(keys.get(n.id));
  }
  for (const n of manual) {
    const md = n.metadata || {};
    if (md.related_note_id) {
      keys.set(n.id, keys.get(md.related_note_id) || 'note:' + md.related_note_id);
      continue;
    }
    const candidates = dayKeys.get(_noteDay(n));
    if (candidates && candidates.size === 1) keys.set(n.id, candidates.values().next().value);
  }
  return keys;
};

// Most informative member first: hand-written recap, then Fireflies
// transcript, then the calendar invite (boilerplate).
const _MEETING_SOURCE_RANK = { fireflies: 1, outlook_calendar: 2 };
const _rankMeetingNotes = (group) =>
  [...group].sort((a, b) =>
    (_MEETING_SOURCE_RANK[a.external_source] || 0) - (_MEETING_SOURCE_RANK[b.external_source] || 0));

// Fold meeting notes sharing a key (>=2) into one meeting item, positioned at
// the newest member (input is newest-first). Runs on groupEmailThreads output,
// so thread items pass through untouched.
const groupMeetingNotes = (items) => {
  if (!items || items.length === 0) return items || [];
  const keys = meetingNoteKeys(items.filter((it) => it.kind === 'note').map((it) => it.note));
  const byKey = new Map();
  for (const it of items) {
    if (it.kind !== 'note') continue;
    const k = keys.get(it.note.id);
    if (!k) continue;
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(it.note);
  }
  const out = [], emitted = new Set();
  for (const it of items) {
    const k = it.kind === 'note' ? keys.get(it.note.id) : null;
    const group = k ? byKey.get(k) : null;
    if (!group || group.length < 2) { out.push(it); continue; }
    if (emitted.has(k)) continue;
    emitted.add(k);
    out.push({ kind: 'meeting', meetingKey: k, notes: _rankMeetingNotes(group), count: group.length });
  }
  return out;
};

const _fmtNoteDate = (d) => new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

// ── Collapsed-card previews ──────────────────────────────────────────────────
// Note cards render collapsed at a uniform size (meta row + one-line title +
// two-line body preview); clicking a card opens the full note in NoteModal.
// Long bodies show a cached AI summary instead of raw text once the server
// has generated one (POST /notes/ai-previews).

const AI_PREVIEW_MIN_CHARS = 500;  // mirrors PREVIEW_MIN_CHARS server-side
const _needsAiPreview = (n) => !n.ai_preview && (n.body || '').length > AI_PREVIEW_MIN_CHARS;

// Fetch (and lazily warm) AI previews for the long notes in a feed. Returns
// {noteId: previewText}. The server generates a bounded batch per call and
// persists it, so we re-request while it reports pending work — capped in
// case a note keeps failing to summarize.
const useNoteAiPreviews = (notes) => {
  const [map, setMap] = React.useState({});
  React.useEffect(() => {
    const ids = (notes || []).filter(_needsAiPreview).map((n) => n.id).slice(0, 60);
    if (ids.length === 0) return undefined;
    let alive = true;
    (async () => {
      for (let round = 0; round < 8 && alive; round++) {
        let res;
        try { res = await window.AtlasApi.noteAiPreviews(ids); } catch (_) { return; }
        if (!alive) return;
        if (res && res.previews && Object.keys(res.previews).length > 0) {
          setMap((m) => ({ ...m, ...res.previews }));
        }
        if (!res || !res.pending) return;
      }
    })();
    return () => { alive = false; };
  }, [notes]);
  return map;
};

// Markdown → inline-formatted preview for the clamped cards. Block structure
// flattens onto one line (bullets become "•", links keep their label, code
// fences drop) but bold / italic / strike / inline code still render, so the
// collapsed card matches the formatting the modal shows. Only the toolbar's
// own syntax (** * ~~ `) is parsed — underscores stay literal because names
// like some_field_name would false-positive after whitespace collapse.
const _INLINE_MD = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|~~[^~\n]+~~|`[^`\n]+`)/g;

const _mdPlain = (s) => (s || '')
  .replace(/```[\s\S]*?```/g, ' ')
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
  .replace(/^[ \t]*>[ \t]?/gm, '')
  .replace(/^[ \t]*(?:[-*]|\d+[.)])[ \t]+/gm, '• ')
  .replace(/\s+/g, ' ')
  .trim();

const _mdInline = (s) => {
  const text = _mdPlain(s);
  if (!text) return null;
  // split() on a single capture group alternates plain text (even indexes)
  // with the matched markdown spans (odd indexes).
  return text.split(_INLINE_MD).map((part, i) => {
    if (i % 2 === 0) return part;
    if (part.startsWith('**')) return <b key={i}>{part.slice(2, -2)}</b>;
    if (part.startsWith('~~')) return <s key={i}>{part.slice(2, -2)}</s>;
    if (part.startsWith('`')) return <code key={i} style={{ fontSize: '0.92em' }}>{part.slice(1, -1)}</code>;
    return <i key={i}>{part.slice(1, -1)}</i>;
  });
};

// ── Record-page note search ──────────────────────────────────────────────────
// Wrap every case-insensitive hit of `term` in <mark>. Returns the text
// untouched when nothing matches (or there's nothing to match).
const _escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlightText = (text, term) => {
  if (!term || !text) return text;
  const parts = String(text).split(new RegExp(`(${_escapeRe(term)})`, 'ig'));
  if (parts.length === 1) return text;
  // split() on one capture group alternates plain (even) / hit (odd).
  return parts.map((part, i) => (i % 2 === 1
    ? <mark key={i} style={{ background: 'var(--accent-soft)', color: 'inherit', fontWeight: 600, borderRadius: 2, padding: '0 1px' }}>{part}</mark>
    : part));
};

// The collapsed body for a search hit: the RAW body (never the AI summary —
// it may not contain the term), windowed so the first hit lands inside the
// two-line clamp. null when the body doesn't contain the term (title-only
// match) so the caller falls back to the normal preview.
const _searchSnippet = (body, term) => {
  const plain = _mdPlain(body);
  if (!plain) return null;
  const idx = plain.toLowerCase().indexOf(term.toLowerCase());
  if (idx < 0) return null;
  if (idx <= 60) return plain;
  let start = idx - 50;
  const sp = plain.lastIndexOf(' ', start);
  if (sp > 0 && start - sp < 20) start = sp + 1;   // don't open mid-word
  return '…' + plain.slice(start);
};

// Compact search box for the record-page note feeds (company / contact /
// deal). Server-side: the term goes to list_notes `q`, so it searches every
// note on the record — not just the pages loaded so far. Escape clears.
const NoteSearchInput = ({ value, onChange }) => {
  const [focused, setFocused] = React.useState(false);
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span style={{ position: 'absolute', left: 7, display: 'inline-flex', color: 'var(--text-4)', pointerEvents: 'none' }}>
        <Icon.Search size={11} />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.stopPropagation(); onChange(''); } }}
        placeholder="Search notes…"
        aria-label="Search notes"
        style={{
          width: focused || value ? 220 : 150, transition: 'width .12s',
          height: 24, padding: '0 20px 0 22px', fontSize: 11.5, fontFamily: 'inherit',
          border: `1px solid ${focused ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 6,
          background: 'var(--surface)', color: 'var(--text)', outline: 'none',
        }}
      />
      {value && (
        <button type="button" onClick={() => onChange('')} title="Clear search" aria-label="Clear search"
          style={{ position: 'absolute', right: 3, width: 16, height: 16, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 999,
            background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', fontSize: 13, lineHeight: 1, padding: 0 }}>
          ×
        </button>
      )}
    </div>
  );
};

// Uniform collapsed body: the AI summary when one exists, else the raw text —
// clamped to two lines either way. With `highlight` (an active search) the
// card shows the matching passage of the raw body instead, hits marked.
const NotePreviewBody = ({ n, aiPreviews, style, highlight }) => {
  const snippet = highlight ? _searchSnippet(n.body, highlight) : null;
  const ai = snippet ? null : ((aiPreviews && aiPreviews[n.id]) || n.ai_preview || null);
  const text = snippet ? highlightText(snippet, highlight) : _mdInline(ai || n.body);
  if (!text) return null;
  return (
    <div style={{ display: 'flex', gap: 5, marginTop: 3, ...style }}>
      {ai && (
        <span title="AI summary — open the note for the full text" style={{
          flexShrink: 0, alignSelf: 'flex-start', marginTop: 2,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
          color: 'var(--accent-text)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '0 4px', lineHeight: '13px',
        }}>AI</span>
      )}
      <div style={{
        fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45, minWidth: 0,
        display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
        overflow: 'hidden', wordBreak: 'break-word',
      }}>{text}</div>
    </div>
  );
};

// Collapsible email-thread card. The collapsed card shows the thread's AI
// summary (POST /email-threads/summary — cached server-side, only regenerates
// when the thread grew), loaded on mount so the feed reads at a glance;
// expanding renders the full message history.
const EmailThreadCard = ({ thread, activeUser, onOpenNote, border }) => {
  const [open, setOpen] = React.useState(false);
  const [summary, setSummary] = React.useState(null);  // null=loading, ''=none, str=loaded
  const latest = thread.latest;
  const when = latest.dc_created_at || latest.created_at;
  const subject = latest.title || '(no subject)';

  React.useEffect(() => {
    let alive = true;
    setSummary(null);
    // A deal guest can't ask for summaries (the endpoint takes any thread id).
    if (window.AtlasIsGuest) { setSummary(''); return undefined; }
    window.AtlasApi.emailThreadSummary(thread.conversationId)
      .then((r) => { if (alive) setSummary((r && r.summary) || ''); })
      .catch(() => { if (alive) setSummary(''); });
    return () => { alive = false; };
  }, [thread.conversationId]);

  const toggle = () => setOpen(!open);

  return (
    <div style={{ borderTop: border ? '1px solid var(--border)' : 'none' }}>
      <div onClick={toggle}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = ''}
        style={{ display: 'flex', gap: 10, padding: '10px 14px', cursor: 'pointer' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: HUE.teal.bg, color: HUE.teal.fg }}>
          <Icon.Mail size={11} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', flexWrap: 'wrap' }}>
            <Pill hue="teal" sz="sm">Email thread</Pill>
            <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{thread.count} messages</span>
            <span>·</span>
            <span>{_fmtNoteDate(when)}</span>
            <span style={{ display: 'inline-flex', color: 'var(--text-4)',
              transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
              <Icon.ChevronR size={11} />
            </span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{subject}</div>
          <div style={{ marginTop: 3, fontSize: 12.5, color: 'var(--text-2)' }}>
            {summary === null && <span style={{ color: 'var(--text-3)' }}>Summarizing thread…</span>}
            {summary === '' && <span style={{ color: 'var(--text-3)' }}>No summary available.</span>}
            {summary && (
              <div style={{ display: 'flex', gap: 5 }}>
                <span title="AI summary of the whole thread" style={{
                  flexShrink: 0, alignSelf: 'flex-start', marginTop: 2,
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                  color: 'var(--accent-text)', border: '1px solid var(--border)',
                  borderRadius: 4, padding: '0 4px', lineHeight: '13px',
                }}>AI</span>
                <div style={open ? { minWidth: 0 } : {
                  minWidth: 0, display: '-webkit-box', WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2, overflow: 'hidden', wordBreak: 'break-word',
                }}>{open ? <Markdown>{summary}</Markdown> : _mdInline(summary)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 14px 10px 46px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {thread.emails.map((m) => {
            const md = m.metadata || {};
            const out = md.direction === 'outbound';
            const mwhen = m.dc_created_at || m.created_at;
            return (
              <div key={m.id} onClick={() => onOpenNote && onOpenNote(m.id)}
                style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                  <Pill hue={out ? 'blue' : 'slate'} sz="sm">{out ? 'Sent' : 'Received'}</Pill>
                  <span style={{ color: 'var(--text-2)' }}>{md.from || m.author_name || 'unknown'}</span>
                  <span>·</span>
                  <span>{_fmtNoteDate(mwhen)}</span>
                  {m.external_url && (
                    <a href={m.external_url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'var(--accent-text)', textDecoration: 'none' }}>open ↗</a>
                  )}
                </div>
                {m.title && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginTop: 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>}
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 1,
                  display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
                  overflow: 'hidden', wordBreak: 'break-word' }}>{_mdInline(m.body)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Small linked source badge (Outlook / Fireflies) — same look as the one on
// a feed row (views/record_notes.jsx), reused per-member on the meeting card.
const NoteSourceBadge = ({ n }) => {
  if (!n.external_source) return null;
  const inner = (
    <>
      {window.IntegrationLogo && window.IntegrationLogo(n.external_source, 12)}
      {(window.IntegrationSourceLabel ? window.IntegrationSourceLabel(n.external_source) : n.external_source)}
      {n.external_url ? ' ↗' : ''}
    </>
  );
  const style = {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px',
    borderRadius: 4, background: 'var(--surface-2)', border: '1px solid var(--border)',
    fontSize: 10.5, color: 'var(--text-2)', fontWeight: 500, textDecoration: 'none',
  };
  return n.external_url ? (
    <a href={n.external_url} target="_blank" rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()} style={style}>{inner}</a>
  ) : <span style={style}>{inner}</span>;
};

// Collapsible card for one meeting's notes (calendar invite + Fireflies
// transcript + hand-written recap). Collapsed it reads like a single note —
// the most informative member provides title/preview, with a source badge per
// synced member; expanding lists every note (click one to open it). `extra`
// sits in the meta row (talk time); `below` under the preview (a folded Next
// Action, views/record_notes.jsx).
const MeetingGroupCard = ({ group, onOpenNote, border, aiPreviews, subtypeFilter, setSubtypeFilter, extra = null, below = null }) => {
  const [open, setOpen] = React.useState(false);
  const primary = group.notes[0];
  // Meeting time: a synced member's dc_created_at is the event start —
  // prefer it over when someone typed their recap.
  const synced = group.notes.find((n) => n.external_source);
  const when = window.NoteCardDate(synced || primary);
  const subtype = group.notes.map((n) => n.dc_note_subtype).find((s) => s && s !== 'Other');
  const author = group.notes.map((n) => n.author_name).find(Boolean);
  const authorId = (group.notes.find((n) => n.author_name) || {}).author_id;

  return (
    <div style={{ borderTop: border ? '1px solid var(--border)' : 'none' }}>
      <div onClick={() => setOpen(!open)}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
        onMouseLeave={(e) => e.currentTarget.style.background = ''}
        style={{ display: 'flex', gap: 10, padding: '10px 14px', cursor: 'pointer' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: 999, flexShrink: 0, background: HUE.violet.bg, color: HUE.violet.fg }}>
          <Icon.Doc size={11} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', flexWrap: 'wrap' }}>
            {author && <UserLink id={authorId} name={author} style={{ color: 'var(--text-2)', fontWeight: 500 }} />}
            {author && <span>·</span>}
            <span>{when.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            {subtype ? (
              <button
                onClick={(e) => { e.stopPropagation(); setSubtypeFilter && setSubtypeFilter(subtype === subtypeFilter ? null : subtype); }}
                style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
                <Pill hue="violet" sz="sm">{subtype}</Pill>
              </button>
            ) : (
              <Pill hue="violet" sz="sm">Meeting</Pill>
            )}
            <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{group.count} notes</span>
            {extra}
            {group.notes.map((n) => <NoteSourceBadge key={n.id} n={n} />)}
            <span style={{ display: 'inline-flex', color: 'var(--text-4)',
              transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
              <Icon.ChevronR size={11} />
            </span>
          </div>
          {primary.title && <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginTop: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary.title}</div>}
          <NotePreviewBody n={primary} aiPreviews={aiPreviews} />
          {below}
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 14px 10px 46px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {group.notes.map((m) => {
            const label = !m.external_source ? 'Note'
              : m.external_source === 'fireflies' ? 'Transcript' : 'Calendar invite';
            return (
              <div key={m.id} onClick={() => onOpenNote && onOpenNote(m.id)}
                style={{ borderLeft: '2px solid var(--border)', paddingLeft: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                  <Pill hue={m.external_source ? 'slate' : 'violet'} sz="sm">{label}</Pill>
                  {m.author_name && <span style={{ color: 'var(--text-2)' }}>{m.author_name}</span>}
                  <span>·</span>
                  <span>{_fmtNoteDate(window.NoteCardDate(m))}</span>
                  {window.isPossibleNoShow(m) && <window.NoShowPill />}
                  <NoteSourceBadge n={m} />
                </div>
                {m.title && <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginTop: 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</div>}
                <NotePreviewBody n={m} aiPreviews={aiPreviews} style={{ marginTop: 1 }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Footer row for the record-page note feeds: shown while the server holds
// more notes than are loaded (usePagedNotes). Shared with the deal page.
const NotesLoadMoreRow = ({ notes, total, loadingMore, onLoadMore }) => {
  if (!notes || total == null || total <= notes.length || !onLoadMore) return null;
  return (
    <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', textAlign: 'center' }}>
      <Btn size="xs" variant="outline" onClick={onLoadMore} disabled={loadingMore}>
        {loadingMore ? 'Loading…' : `Load older notes · showing ${notes.length} of ${total}`}
      </Btn>
    </div>
  );
};

// ============ Right rail: related engagements + contacts ============
// Buyer/seller side of an engagement (engagement_type). Hues mirror the
// company role pills (seller=teal, buyer=violet) so the two read as one system.
const ENG_TYPE_PILL = {
  seller_buyside: { label: 'Seller',    hue: 'teal' },
  buyer_buyside:  { label: 'Buyer',     hue: 'violet' },
  sell_side:      { label: 'Sell-side', hue: 'blue' },
};

const EngagementRow = ({ e, onOpen, dim }) => (
  <button key={e.id} onClick={() => onOpen(e.id)} style={{
    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px',
    background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
    cursor: 'pointer', textAlign: 'left', opacity: (e.archived || dim) ? 0.55 : 1,
  }}
    onMouseEnter={(ev) => ev.currentTarget.style.background = 'var(--surface-2)'}
    onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* A buyer shell is named after the buyer (= this company), so lead
          with the deal it pursues instead of repeating the company name. */}
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {e.type === 'buyer_buyside' && e.sellerEngagementName ? `Pursuit of ${e.sellerEngagementName}` : e.name}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
        {ENG_TYPE_PILL[e.type] && <span style={{ marginRight: 6 }}><Pill hue={ENG_TYPE_PILL[e.type].hue} sz="sm">{ENG_TYPE_PILL[e.type].label}</Pill></span>}
        <Pill hue="slate" sz="sm">{e.stageName || e.stage}</Pill>
        {e.onHold && <span style={{ marginLeft: 6 }}><Pill hue="amber" sz="sm">on hold</Pill></span>}
        {e.archived && <span style={{ marginLeft: 6 }}><Pill hue="slate" sz="sm">{e.archivedReason === 'reconciled_duplicate' ? 'archived · duplicate' : 'archived'}</Pill></span>}
      </div>
    </div>
    <span className="num" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{fmtMoney(e.size)}</span>
    <Icon.ChevronR size={12} color="var(--text-4)" />
  </button>
);

// ── Ownership: what we know about who bought / renamed this company ─────────
//
// company_acquisitions rows for THIS company — the portfolio scrape, the web
// signals and the website pick all write there, but until now they surfaced
// only on Buyers → Acquisitions. A dealmaker looking at PestRoutes could not
// see "rebranded to FieldRoutes, pending review" on the record itself. Confirm
// flips Acquired (audited as the user, via the existing decide endpoint);
// Reject leaves the company alone. A plain rebrand is shown but never flips
// the flag on its own — confirming it is the human's call.
const OWN_HUE = { privately_held: 'green', retarget: 'amber', dq: 'rose', unsure: 'slate' };
const OWN_OUTCOMES = [
  { value: 'privately_held', label: 'Privately held' }, { value: 'retarget', label: 'Retarget' },
  { value: 'dq', label: 'DQ' }, { value: 'unsure', label: 'Unsure' },
];
const OWN_REASONS = ['public', 'funding', 'franchised', 'nonprofit', 'government', 'pe_backed', 'closed', 'acquired', 'subsidiary'];
const ownAgo = (iso) => {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : d < 30 ? `${d} days ago` : `${Math.round(d / 30)} months ago`;
};

// The check's sources and thought process, the research wand's card
// (views/research.jsx) for ownership: what the check concluded, each step's
// reasoning, the pages the verdict rests on, and every page and search it
// went through (`steps`, 202610081500). Live while the check runs.
const OWN_CONF = { high: 'green', medium: 'amber', low: 'slate' };
const OwnershipCard = ({ own, at, onClose, onToggleSteps, onRecheck }) => {
  useEscapeKey(true, onClose);
  const running = ['queued', 'running'].includes(own.status);
  const done = own.status === 'done';
  const steps = own.steps || [];
  const Steps = window.RunSteps;
  // Confidence, sources and the acquired-by / set-by-hand lines: the research
  // drawer's ownership mapping (research.jsx), one copy for both.
  const view = window.RESEARCH_VIEWS ? window.RESEARCH_VIEWS.ownership(own) : null;
  const conf = view && view.confidence;
  const sources = (view && view.sources) || [];
  const Facts = window.OwnershipFacts;
  const width = Math.min(420, window.innerWidth - 24);
  const top = Math.max(12, Math.min(at.y + 12, window.innerHeight - 460));
  const heading = (t) => (
    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
      color: 'var(--text-4)', margin: '10px 0 4px' }}>{t}</div>
  );
  return ReactDOM.createPortal(
    <>
      <div onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 400 }} />
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Ownership check" style={{
        position: 'fixed', left: Math.max(12, Math.min(at.x - 20, window.innerWidth - width - 12)), top, zIndex: 401,
        width, maxHeight: Math.min(560, window.innerHeight - top - 12), overflowY: 'auto',
        background: 'var(--surface)', border: '1px solid var(--border-strong)', borderRadius: 10,
        boxShadow: 'var(--shadow-md, var(--shadow-sm))', padding: '10px 12px 12px',
        fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <Icon.Wand size={13} color="var(--accent-text)" />
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>Ownership check</span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} title="Close" style={{ border: 'none', background: 'transparent',
            cursor: 'pointer', color: 'var(--text-3)', padding: 2, display: 'inline-flex' }}>
            <Icon.Close size={12} />
          </button>
        </div>

        {running && (steps.length > 0 && Steps
          ? <Steps steps={steps} live />
          : <div style={{ color: 'var(--text-3)' }}>Starting…</div>)}

        {done && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{own.final_label || 'No verdict'}</span>
              {conf && <Pill hue={OWN_CONF[conf] || 'slate'} sz="sm">{conf} confidence</Pill>}
            </div>
            {Facts && <Facts own={own} />}
            {own.reasoning && (
              <>
                {own.step2_reasoning && heading('Ownership')}
                <div style={{ marginTop: own.step2_reasoning ? 0 : 8, whiteSpace: 'pre-wrap' }}>{own.reasoning}</div>
              </>
            )}
            {own.step2_reasoning && (
              <>
                {heading('Ultimate parent')}
                <div style={{ whiteSpace: 'pre-wrap' }}>{own.step2_reasoning}</div>
              </>
            )}
            {sources.length > 0 && window.RunSources && (
              <>
                {heading('Sources')}
                <window.RunSources sources={sources} />
              </>
            )}
          </>
        )}
        {own.status === 'failed' && (
          <div style={{ color: 'var(--rose-fg, var(--text))' }}>The check failed: {own.error || 'unknown error'}</div>
        )}

        {!running && steps.length > 0 && Steps && (
          <>
            <button onClick={onToggleSteps} style={{ border: 'none', background: 'transparent', padding: 0,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, margin: '10px 0 0',
              fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-4)' }}>
              {at.showSteps ? <Icon.ChevronD size={10} /> : <Icon.ChevronR size={10} />}
              How it got here · {steps.length} step{steps.length === 1 ? '' : 's'}
            </button>
            {at.showSteps && <Steps steps={steps} />}
          </>
        )}

        {!running && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <Btn variant="outline" size="sm" onClick={onRecheck}>Check again</Btn>
            {own.checked_at && (
              <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
                Checked {ownAgo(own.checked_at)}{own.source === 'clay' ? ' · from Clay' : ''}
              </span>
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};

const ownLinkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'var(--accent-text)', display: 'inline-flex', alignItems: 'center', gap: 3 };
const ownPillBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex' };

// The ownership status (202609232100): Clay's two-step researcher run in
// Atlas — privately held / DQ (public, funded, franchised, PE-backed…) /
// retarget (acquired) — with the sentence and the page it rests on, a
// Refresh, and a dealmaker's override. A buyer never has one. While a check
// runs, its last few steps show here; the card has the whole trail.
const OwnershipStatus = ({ companyId, isBuyer }) => {
  const [own, setOwn] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({});
  const [busy, setBusy] = React.useState(null);
  const [card, setCard] = React.useState(null);   // { x, y, showSteps } while the card is open
  const load = React.useCallback(() => {
    // A buyer gets no ownership check (rendered null below) — don't ask for one.
    if (!companyId || isBuyer || !window.AtlasApi.companyOwnership) return;
    window.AtlasApi.companyOwnership(companyId).then((r) => setOwn(r || { exists: false })).catch(() => setOwn((o) => o || { exists: false }));
  }, [companyId, isBuyer]);
  React.useEffect(load, [load]);
  const running = Boolean(own && ['queued', 'running'].includes(own.status));
  // A tick, not a chain of timeouts: a failed poll must still schedule the next.
  React.useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [running, load]);
  // The research drawer's "Check again": re-read so the pill and the live trail follow.
  React.useEffect(() => {
    const onChanged = (e) => {
      const d = (e && e.detail) || {};
      const nid = window.RecordPanelsCore.normId;   // case-blind ids; the core loads first
      if (d.src === 'ownership' && d.companyId && companyId && nid(d.companyId) === nid(companyId)) load();
    };
    window.addEventListener('atlas-research-changed', onChanged);
    return () => window.removeEventListener('atlas-research-changed', onChanged);
  }, [companyId, load]);
  if (isBuyer) return null;
  const steps = (own && own.steps) || [];
  const openCard = (e) => { e.stopPropagation(); setCard({ x: e.clientX, y: e.clientY, showSteps: false }); };
  // The verdict in full opens in the research drawer; the card stays the fallback
  // (no drawer here) and the live-run trail.
  const openRun = (e) => { e.stopPropagation(); if (!(window.openResearchRun && window.openResearchRun({ src: 'ownership' }))) openCard(e); };
  const refresh = async () => {
    setBusy('refresh');
    try { await window.AtlasApi.refreshCompanyOwnership(companyId); setOwn((o) => ({ ...(o || {}), exists: true, status: 'queued', steps: [] })); }
    catch (e) { pushToast({ type: 'error', title: 'Could not start the check', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const save = async () => {
    setBusy('save');
    try {
      const r = await window.AtlasApi.patchCompanyOwnership(companyId, { manual_outcome: draft.manual_outcome || null,
        manual_reason: draft.manual_outcome ? (draft.manual_reason || null) : null, manual_note: draft.manual_note || null });
      setOwn({ exists: true, ...r }); setEditing(false); pushToast({ type: 'success', title: 'Ownership saved' });
    } catch (e) { pushToast({ type: 'error', title: 'Could not save', body: String(e.message || e) }); }
    finally { setBusy(null); }
  };
  const outcome = own && own.effective_outcome;
  const evidenceUrl = own && (own.ultimate_parent ? own.step2_evidence_url : own.evidence_url);
  const evidenceText = own && (own.ultimate_parent ? own.step2_evidence_text : own.evidence_text);
  return (
    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Ownership</span>
        {outcome && (own.final_outcome || steps.length > 0
          ? <button type="button" onClick={openRun} title="Sources & reasoning" style={{ ...ownPillBtn, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}<Pill hue={OWN_HUE[outcome] || 'slate'} sz="sm">{own.outcome_label}{own.reason_label ? ` · ${own.reason_label}` : ''}</Pill></button>
          : <Pill hue={OWN_HUE[outcome] || 'slate'} sz="sm">{own.outcome_label}{own.reason_label ? ` · ${own.reason_label}` : ''}</Pill>)}
        {own && own.manual_outcome && <Pill hue="violet" sz="sm" title={own.manual_note || 'Set by hand'}>set by hand</Pill>}
        {running && <span style={{ fontSize: 11, color: 'var(--text-3)' }}>checking…</span>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {own && own.exists && own.status === 'done' && !editing && (
            <Btn size="xs" variant="ghost" onClick={() => { setDraft({ manual_outcome: own.manual_outcome || '', manual_reason: own.manual_reason || '', manual_note: own.manual_note || '' }); setEditing(true); }}>Override</Btn>
          )}
          <Btn size="xs" variant="ghost" onClick={refresh} disabled={busy === 'refresh' || running}
            icon={<span style={{ display: 'inline-flex', animation: running ? 'atlasResearchPulse 1.2s ease-in-out infinite' : 'none' }}><Icon.Wand size={11} /></span>}
            title="Re-run the ownership check">
            {own && own.exists && own.status === 'done' ? 'Refresh' : 'Check ownership'}
          </Btn>
        </span>
      </div>
      {own && own.exists && own.status === 'done' && !editing && (
        <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 6, lineHeight: 1.5 }}>
          {own.acquired_by && <div>Acquired by <b>{own.acquired_by}</b>{own.ultimate_parent && own.ultimate_parent !== own.acquired_by ? <> · ultimate parent <b>{own.ultimate_parent}</b></> : null}
            {own.ultimate_parent_status && own.ultimate_parent_status !== 'unknown' && <span style={{ color: 'var(--text-3)' }}> ({own.ultimate_parent_status.replace('_', ' ')})</span>}
            {own.sponsor_buyer_id && <> · <button onClick={() => window.AtlasNav?.goToBuyer?.(own.sponsor_buyer_id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}on our buyer roster</button></>}
          </div>}
          {evidenceText && <div style={{ color: 'var(--text-3)', marginTop: 2 }}>“{evidenceText}”</div>}
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {evidenceUrl && <a href={evidenceUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{evidenceUrl.replace(/^https?:\/\/(www\.)?/, '').slice(0, 60)}</a>}
            {(own.step2_confidence || own.confidence) && <span>{(own.step2_confidence || own.confidence)} confidence</span>}
            <span>{own.source === 'clay' ? 'from Clay' : 'checked by Atlas'}{own.checked_at ? ` · ${ownAgo(own.checked_at)}` : ''}</span>
            {(own.final_outcome || steps.length > 0) && (
              <button onClick={openRun} style={{ ...ownLinkBtn, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}<Icon.Sparkle size={10} />Sources & reasoning</button>
            )}
          </div>
          {own.manual_note && <div style={{ color: 'var(--text-3)', marginTop: 3 }}>Note: {own.manual_note}</div>}
        </div>
      )}
      {running && (
        <div style={{ fontSize: 12, marginTop: 6 }}>
          {steps.length > 0 && window.RunSteps
            ? <window.RunSteps steps={steps.slice(-3)} live />
            : <div style={{ color: 'var(--text-3)' }}>Starting…</div>}
          {steps.length > 3 && (
            <button onClick={openCard} style={{ ...ownLinkBtn, fontSize: 11, marginTop: 4 }}>All {steps.length} steps</button>
          )}
        </div>
      )}
      {own && own.status === 'failed' && (
        <div style={{ fontSize: 12, color: 'var(--red-fg, #b00)', marginTop: 6 }}>
          Last check failed: {own.error}
          {steps.length > 0 && <> · <button onClick={openCard} style={ownLinkBtn}>See the steps</button></>}
        </div>
      )}
      {card && own && (
        <OwnershipCard own={own} at={card} onClose={() => setCard(null)}
          onToggleSteps={() => setCard((c) => c && { ...c, showSteps: !c.showSteps })}
          onRecheck={refresh} />
      )}
      {own && !own.exists && !running && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>Not checked yet.</div>}
      {editing && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Outcome
            <window.Picker value={draft.manual_outcome} onChange={(v) => setDraft({ ...draft, manual_outcome: v })} size="sm" placeholder="Outcome"
              emptyLabel="Use the model's answer" options={OWN_OUTCOMES} />
          </label>
          {draft.manual_outcome && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>Reason
              <window.Picker value={draft.manual_reason} onChange={(v) => setDraft({ ...draft, manual_reason: v })} size="sm" placeholder="Reason"
                emptyLabel="—" options={OWN_REASONS.map((r) => ({ value: r, label: r.replace('_', ' ') }))} />
            </label>
          )}
          <input value={draft.manual_note} onChange={(e) => setDraft({ ...draft, manual_note: e.target.value })} placeholder="Why (optional)" style={{ font: 'inherit', padding: '4px 6px' }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn size="xs" onClick={save} disabled={busy === 'save'}>Save</Btn>
            <Btn size="xs" variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

// What a confirmed acquisition did (buyer_acquisitions.close_out_acquisition):
// the deals it exited, any it could not, and the note. Shared with the Buyers
// page's Acquisitions tab.
const acquisitionToast = (r, out) => {
  const exited = (out && out.exited) || [];
  const open = (out && out.not_exited) || [];
  const parts = [];
  if (exited.length) {
    parts.push(`Exited ${exited.length === 1 ? `“${exited[0].name}”` : `${exited.length} engagements`} as Company sold.`);
  }
  if (out && out.note_id) parts.push('Note added with the source.');
  else if (out && !out.is_acquired_changed) parts.push('It was already flagged.');
  if (open.length) parts.push(`Still open, exit by hand: ${open.map((d) => d.name).join(', ')}.`);
  pushToast({ type: open.length ? 'warning' : 'success', title: `${r.company_name} marked acquired`, body: parts.join(' ') });
};

const OwnershipPanel = ({ companyId, company, buyer, onDecided }) => {
  const [rows, setRows] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const load = React.useCallback(() => {
    if (!companyId) return;
    window.AtlasApi.buyerAcquisitions({ company_id: companyId, state: 'all', limit: 20 })
      .then((r) => setRows((r && r.items) || []))
      .catch(() => setRows([]));
  }, [companyId]);
  React.useEffect(load, [load]);
  const roles = (company && company._raw && company._raw.roles) || [];
  // buyer: the roster row — undefined while the page is still asking, null
  // when the company isn't on the roster. Until it's known, treat it as a
  // buyer so the ownership check isn't fetched for a roster firm.
  const isBuyer = roles.includes('buyer') || (company && company._raw && ['buyer', 'both'].includes(company._raw.role))
    || buyer !== null;
  const decide = async (r, decision) => {
    // Confirm exits the company's live deals as "Company sold" (final, pushed
    // to DealCloud) — say so before doing it, never after.
    if (decision === 'confirmed' && r.live_deals > 0) {
      const ok = await showConfirm({
        title: 'Confirm the acquisition?',
        body: `This marks ${r.company_name} acquired${r.acquirer_name ? ` by ${r.acquirer_name}` : ''} and exits its `
          + `${r.live_deals === 1 ? 'live engagement' : `${r.live_deals} live engagements`} as "Company sold". `
          + `A note with the source goes on the company${r.live_deals === 1 ? ' and the engagement' : ''}.`,
        confirmLabel: r.live_deals === 1 ? 'Confirm & exit deal' : 'Confirm & exit deals',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(r.id);
    try {
      const out = await window.AtlasApi.decideBuyerAcquisition(r.id, decision);
      load();
      if (onDecided) await onDecided();
      if (decision === 'confirmed') acquisitionToast(r, out);
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save', body: e.message || String(e) });
    } finally {
      setBusy(null);
    }
  };
  const list = rows || [];
  const pending = list.filter((r) => !r.decision);
  const rel = (r) => {
    const ev = (r.evidence || '').toLowerCase();
    if (ev.startsWith("the company's website now carries a new name") || ev.includes('rebrand')) return 'Rebrand';
    if (ev.includes('merged')) return 'Merger';
    return 'Acquisition';
  };
  return (
    <>
      <OwnershipStatus companyId={companyId} isBuyer={isBuyer} />
      {list.length > 0 && (
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Acquisition records</span>
            {pending.length > 0 && <Pill hue="amber" sz="sm">{pending.length} to review</Pill>}
            {company?._raw?.is_acquired && <Pill hue="rose" sz="sm">acquired</Pill>}
          </div>
          {list.map((r) => (
            <div key={r.id} style={{ padding: '8px 0', borderTop: '1px solid var(--border-2, var(--border))', fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>{rel(r)}</span>
                {r.acquirer_name && <span style={{ color: 'var(--text-2)' }}>
                  {rel(r) === 'Rebrand' ? 'now' : 'by'}{' '}
                  {r.acquirer_company_id
                    ? <a href={`#/companies/${r.acquirer_company_id}`} style={{ color: 'var(--accent-text)' }}>{r.acquirer_name}</a>
                    : r.acquirer_name}
                </span>}
                {r.announced_year && <span style={{ color: 'var(--text-3)' }}>· {r.announced_year}</span>}
                <Pill hue={r.decision === 'confirmed' ? 'green' : r.decision === 'rejected' ? 'slate' : 'amber'} sz="sm">
                  {r.decision || `pending · ${r.confidence}`}
                </Pill>
              </div>
              {r.evidence && <div style={{ color: 'var(--text-3)', marginTop: 3, lineHeight: 1.4 }}>{r.evidence}</div>}
              {/* Source on one line (the URL truncates, the label never wraps
                  under it), then the decision on its own row. */}
              <div style={{ display: 'flex', gap: 6, marginTop: 5, alignItems: 'center', fontSize: 11.5, minWidth: 0 }}>
                {r.source_url && (
                  <a href={r.source_url} target="_blank" rel="noopener noreferrer" title={r.source_url}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, color: 'var(--accent-text)', textDecoration: 'none', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
                    {window.PhoneHit && <window.PhoneHit />}
                    <Icon.External size={10} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.source_url.replace(/^https?:\/\/(www\.)?/, '')}
                    </span>
                  </a>
                )}
                <span style={{ color: 'var(--text-4)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {r.source_url ? '· ' : ''}{(r.source || '').replace('_', ' ')}
                </span>
              </div>
              {!r.decision && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Btn size="xs" variant="accent" disabled={busy === r.id} onClick={() => decide(r, 'confirmed')}
                    icon={<Icon.Check size={11} />}
                    title={r.live_deals > 0 ? 'Mark acquired, exit the live deal as Company sold and log a note' : 'Mark acquired and log a note'}>
                    {busy === r.id ? 'Saving…' : 'Confirm'}
                  </Btn>
                  <Btn size="xs" variant="outline" disabled={busy === r.id} onClick={() => decide(r, 'rejected')}
                    icon={<Icon.Close size={10} />} title="Wrong company, or not an acquisition">
                    Reject
                  </Btn>
                  {r.live_deals > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      exits {r.live_deals === 1 ? 'the live deal' : `${r.live_deals} live deals`}
                    </span>
                  )}
                </div>
              )}
              {r.decision && (r.decided_by_name || r.decided_at) && (
                <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>
                  {r.decision === 'confirmed' ? 'Confirmed' : 'Rejected'}
                  {r.decided_by_name ? ` by ${r.decided_by_name}` : (r.decision === 'confirmed' ? ' automatically' : '')}
                  {r.decided_at ? ` · ${new Date(r.decided_at).toLocaleDateString()}` : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const RelatedEngagementsPanel = ({ engagements, onOpen, onCreate }) => {
  // Buy-side shells are DealCloud bookkeeping (one record per pursuit, named
  // after this company) — real deals they map to live in "Buying into" below.
  // Keep them reachable but out of the way so the panel reads as THIS
  // company's own deals.
  const [showShells, setShowShells] = React.useState(false);
  const deals  = engagements && engagements.filter((e) => e.type !== 'buyer_buyside');
  const shells = engagements ? engagements.filter((e) => e.type === 'buyer_buyside') : [];
  // Live engagements first — an archived duplicate should never be the row
  // someone lands on when both share the company's name.
  const sorted = deals && [...deals].sort((a, b) => (a.archived ? 1 : 0) - (b.archived ? 1 : 0));
  return (
  <div>
    <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <Icon.Pipeline size={13} color="var(--accent)" />
      <span style={{ fontSize: 13, fontWeight: 600 }}>Engagements</span>
      <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{deals ? deals.length : ''}</span>
      {onCreate && (
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={onCreate}>New</Btn>
      )}
    </div>
    {engagements === null && <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
    {deals && deals.length === 0 && (
      <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
        {shells.length > 0 ? 'No deals with this company as the client.' : 'No engagements at this company.'}
      </div>
    )}
    {sorted && sorted.map((e) => <EngagementRow key={e.id} e={e} onOpen={onOpen} />)}
    {shells.length > 0 && (
      <>
        <button onClick={() => setShowShells((s) => !s)} style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '7px 14px',
          background: 'var(--surface-2)', border: 'none', borderBottom: '1px solid var(--border)',
          cursor: 'pointer', textAlign: 'left', fontSize: 11, color: 'var(--text-3)',
        }}>
          <Icon.ChevronR size={10} style={{ transform: showShells ? 'rotate(90deg)' : 'none' }} />
          <span style={{ fontWeight: 600 }}>Deal pursuits · {shells.length}</span>
        </button>
        {showShells && shells.map((e) => <EngagementRow key={e.id} e={e} onOpen={onOpen} dim />)}
      </>
    )}
  </div>
  );
};

// Buyers interested in THIS company (seller side) — DealCloud shows this
// table on the Company view, so mirror it here. Full buyer board with blast/
// BS-call detail lives on the seller engagement page; the name opens the
// buyer firm, the header button opens the deal. Each buyer's pursuit stage is
// editable in place (window.PursuitStageCell, shared with the deal board):
// the pick reaches DealCloud and Lost moves the buyer to passed.
// `sectionId` keys the remembered fold (the phone page passes its own).
const InterestedBuyersPanel = ({ buyers, dealNames, onOpenDeal, onRowUpdate, sectionId = 'company.interested_buyers' }) => {
  if (!buyers || buyers.length === 0) return null;  // hide for non-sellers / no interest yet
  const intHue = { high: 'green', medium: 'amber', low: 'slate', passed: 'rose' };
  const passed = buyers.filter((b) => b.interest_level === 'passed').length;
  // Live interest first; passed buyers sink and dim.
  const sorted = [...buyers].sort((a, b) =>
    (a.interest_level === 'passed' ? 1 : 0) - (b.interest_level === 'passed' ? 1 : 0));
  const PursuitStageCell = window.PursuitStageCell;
  const Section = window.Section;
  if (!Section) return null;
  const counts = `${buyers.length - passed} live${passed > 0 ? ` · ${passed} passed` : ''}`;
  // Collapsible like "Acquired nearby" (open by default; the fold is
  // remembered per viewer by Section under its id).
  return (
    <Section title={`Interested buyers · ${buyers.length}`} collapsible id={sectionId}
      preview={counts}
      action={onOpenDeal && buyers[0] && (
        <Btn size="xs" variant="outline" onClick={() => onOpenDeal(buyers[0].engagement_id)}>Board</Btn>
      )}
      style={{ border: 'none', borderRadius: 0, borderBottom: '1px solid var(--border)', background: 'transparent' }}>
      <div style={{ padding: '6px 14px', fontSize: 10.5, color: 'var(--text-4)' }}>
        {counts}
      </div>
      {sorted.map((b) => (
        <div key={b.id}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: rowPhone() ? '8px 14px 12px' : '8px 14px',
            borderTop: '1px solid var(--border)',
          }}
          onMouseEnter={(ev) => ev.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
        >
          <div style={{ flex: 1, minWidth: 0, opacity: b.interest_level === 'passed' ? 0.6 : 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <span onClick={() => window.AtlasNav?.goToCompany?.(b.buyer_company_id)}
                title={`Open ${b.buyer_name || 'buyer'}`}
                onMouseOver={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
                onMouseOut={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
                style={{ cursor: 'pointer', textUnderlineOffset: 2 }}>{b.buyer_name || '—'}</span>
              {b.buyer_total_deals > 1 && (
                <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 400, color: 'var(--text-4)' }}>· {b.buyer_total_deals} deals</span>
              )}
            </div>
            {/* Phone: 12px down, so the stage pill's and ↗'s 44px boxes stop below the name above. */}
            <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: rowPhone() ? 12 : 2 }}>
              {PursuitStageCell && (
                <PursuitStageCell b={b} dealName={(dealNames || {})[b.engagement_id]} maxW={150}
                  onSaved={(row) => onRowUpdate && onRowUpdate(b.id, row)} />
              )}
              {b.interest_level && (
                <span title={b.interest_level === 'passed' && b.pass_reason
                  ? `Pass reason: ${b.pass_reason === 'other' && b.pass_detail ? b.pass_detail : b.pass_reason.replace(/_/g, ' ')}` : undefined}>
                  <Pill hue={intHue[b.interest_level] || 'slate'} sz="sm">{b.interest_level}</Pill>
                </span>
              )}
              {b.nda_status && b.nda_status !== 'not_sent' && <Pill hue={b.nda_status === 'signed' ? 'green' : 'amber'} sz="sm">NDA {b.nda_status}</Pill>}
              {b.loi_submitted && <Pill hue="blue" sz="sm">LOI</Pill>}
            </div>
          </div>
          <button onClick={() => window.AtlasNav?.goToCompany?.(b.buyer_company_id)} title={`Open ${b.buyer_name || 'buyer'}`}
            style={{ background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', display: 'inline-flex', ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>
            {window.PhoneHit && <window.PhoneHit />}
            <Icon.ChevronR size={12} color="var(--text-4)" />
          </button>
        </div>
      ))}
    </Section>
  );
};

// Deals this company is buying into. Rows link to the seller engagement the
// buy-side interest belongs to; the target (seller) company is shown as the
// secondary line so a buyer interested in several targets is legible.
// SourceScrub firmographics. enrichment_data.sourcescrub is written two ways:
// mirrored from DealCloud's native SS integration by the hourly sync, or
// pulled live from the Data Connect API via the Enrich button / on create.
// Hidden entirely when there's no data AND the live API isn't configured.
// `readOnly` (the Company intel drawer for a viewer, step 6): no Enrich /
// Refresh / + Add and no enrich-status read. `fallback` (the same drawer):
// rendered instead of nothing when there is no data and nothing to enrich.
// Both default off.
const SourceScrubPanel = ({ companyId, company, contacts, onCompanyChange, onContactCreated, readOnly = false, fallback = null }) => {
  const ss = ((company._raw && company._raw.enrichment_data) || {}).sourcescrub || null;
  const [status, setStatus]   = React.useState(null); // {configured, domain, enriched}
  const [busy, setBusy]       = React.useState(false);
  const [result, setResult]   = React.useState(null); // last enrich response
  const [err, setErr]         = React.useState(null);
  const [addedIds, setAddedIds] = React.useState({});

  React.useEffect(() => {
    if (readOnly) return undefined;      // nothing to enrich: no status read
    let live = true;
    window.AtlasApi.companyEnrichStatus(companyId)
      .then((s) => { if (live) setStatus(s); })
      .catch(() => {});
    return () => { live = false; };
  }, [companyId, readOnly]);

  const canEnrich = !!(status && status.configured && (status.domain || (ss && ss.id)));
  if (!ss && !canEnrich) return fallback;

  const runEnrich = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await window.AtlasApi.enrichCompany(companyId);
      setResult(res);
      if (res && res.found) {
        const fresh = await window.AtlasApi.getCompany(companyId);
        onCompanyChange && onCompanyChange(fresh);
      }
    } catch (e) {
      setErr(String((e && e.message) || e));
    }
    setBusy(false);
  };

  const existingEmails = new Set((contacts || []).map((c) => (c._raw?.email || '').toLowerCase()).filter(Boolean));
  const addContact = async (sc) => {
    const key = sc.contactId || sc.email || `${sc.firstName}|${sc.lastName}`;
    try {
      const created = await window.AtlasApi.createContact({
        company_id: companyId,
        first_name: sc.firstName || '?',
        last_name: sc.lastName || '?',
        title: sc.title || null,
        email: sc.email || null,
        linkedin_url: sc.linkedIn || null,
        contact_type: company._raw?.role === 'service_provider' ? 'professional'
          : ['seller', 'buyer'].includes(company._raw?.role) ? company._raw.role : 'seller',
      });
      setAddedIds((prev) => ({ ...prev, [key]: true }));
      onContactCreated && onContactCreated(created);
    } catch (e) {
      setErr(String((e && e.message) || e));
    }
  };

  // Growth rates from the live API are decimal fractions; the DC-mirrored
  // growth_score is an absolute index — render whichever is present.
  const pct = (v) => (v == null ? null : `${Math.round(v * 1000) / 10}%`);
  const growth = ss && (
    ss.growth_12m != null ? `${pct(ss.growth_12m)} (12mo)` :
    ss.growth_score != null ? `${ss.growth_score}${ss.growth_time_period ? ` (${ss.growth_time_period}mo)` : ''}` : null
  );
  const employees = ss && (ss.employee_count != null
    ? `${ss.employee_count}${ss.employee_range ? ` (${ss.employee_range})` : ''}`
    : ss.employee_range || null);
  const funding = ss && (ss.total_funding ? fmtMoney(ss.total_funding) : null);
  const round = ss && (ss.latest_round_amount
    ? `${fmtMoney(ss.latest_round_amount)}${ss.latest_round_date ? ` · ${ss.latest_round_date}` : ''}` : null);
  const specialties = (ss && ss.specialties)
    ? (Array.isArray(ss.specialties) ? ss.specialties : String(ss.specialties).split(',').map((s) => s.trim()).filter(Boolean))
    : [];
  const links = ss ? [
    ['LinkedIn',   ss.linkedin_url],
    ['Crunchbase', ss.crunchbase_url],
    ['Twitter',    ss.twitter_url],
    ['Facebook',   ss.facebook_url],
  ].filter(([, u]) => u) : [];
  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 12 }}>
      <span style={{ width: 92, flexShrink: 0, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ color: 'var(--text-2)', minWidth: 0 }}>{children}</span>
    </div>
  );
  const ssContacts = (result && result.contacts) || [];

  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <div style={{ padding: '12px 14px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon.Diamond size={13} color="var(--accent)" />
          <span style={{ fontSize: 13, fontWeight: 600 }}>SourceScrub</span>
          {ss && ss.description && (
            <span title={ss.description} style={{
              width: 15, height: 15, borderRadius: '50%', border: '1px solid var(--border)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: 'var(--text-3)', cursor: 'help',
            }}>?</span>
          )}
          {canEnrich && !readOnly && (
            <button onClick={runEnrich} disabled={busy} style={{
              marginLeft: 'auto', fontSize: 10.5, padding: '3px 8px', borderRadius: 5,
              border: '1px solid var(--border)', background: 'var(--surface)',
              color: 'var(--accent-text)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, ...(window.phoneHitHost ? window.phoneHitHost() : null),
            }}>{window.PhoneHit && <window.PhoneHit />}{busy ? 'Enriching…' : ss ? 'Refresh' : 'Enrich'}</button>
          )}
        </div>
        {err && <div style={{ fontSize: 11, color: 'var(--rose-text, #b91c1c)', marginTop: 6, overflowWrap: 'anywhere' }}>{err}</div>}
        {result && result.found === false && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
            No SourceScrub match for {result.identifier}.
          </div>
        )}
        {ss ? (
          <div style={{ marginTop: 6 }}>
            {employees && <Row label="Employees">{employees}</Row>}
            {ss.year_founded && <Row label="Founded">{ss.year_founded}</Row>}
            {ss.ownership_type && <Row label="Ownership">{ss.ownership_type}</Row>}
            {growth && <Row label="Growth">{growth}</Row>}
            {funding && <Row label="Total funding">{funding}</Row>}
            {round && <Row label="Latest round">{round}</Row>}
            {ss.job_openings != null && <Row label="Job openings">{ss.job_openings}</Row>}
            {links.length > 0 && (
              <Row label="Links">
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {links.map(([label, url]) => (
                    <a key={label} href={url} target="_blank" rel="noreferrer" style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      fontSize: 11.5, color: 'var(--accent-text)', textDecoration: 'none', ...(window.phoneHitHost ? window.phoneHitHost() : null),
                    }}>{window.PhoneHit && <window.PhoneHit />}<Icon.External size={11} /> {label}</a>
                  ))}
                </span>
              </Row>
            )}
            {specialties.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                {specialties.slice(0, 10).map((s) => (
                  <Pill key={s} hue="slate" sz="sm">
                    {/* Pill is white-space:nowrap — clamp long SS keyword phrases
                        so one verbose specialty can't force the column to h-scroll. */}
                    <span title={s} style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{s}</span>
                  </Pill>
                ))}
                {specialties.length > 10 && (
                  <span title={specialties.slice(10).join(', ')} style={{ fontSize: 10.5, color: 'var(--text-4)', alignSelf: 'center', cursor: 'help' }}>
                    +{specialties.length - 10} more
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 6 }}>
            Not enriched yet.
          </div>
        )}
        {ssContacts.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', marginBottom: 4 }}>
              Contacts at SourceScrub ({ssContacts.length})
            </div>
            {ssContacts.map((sc) => {
              const key = sc.contactId || sc.email || `${sc.firstName}|${sc.lastName}`;
              const already = addedIds[key] || (sc.email && existingEmails.has(String(sc.email).toLowerCase()));
              return (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ color: 'var(--text-2)' }}>{sc.firstName} {sc.lastName}</span>
                    {sc.title && <span style={{ color: 'var(--text-4)' }}> · {sc.title}</span>}
                    {sc.email && <div style={{ fontSize: 11, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sc.email}{sc.emailConfirmed ? ' ✓' : ''}</div>}
                  </div>
                  {already ? (
                    <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>added</span>
                  ) : readOnly ? null : (
                    <button onClick={() => addContact(sc)} style={{
                      fontSize: 10.5, padding: '2px 7px', borderRadius: 5, border: '1px solid var(--border)',
                      background: 'var(--surface)', color: 'var(--accent-text)', cursor: 'pointer',
                    }}>+ Add</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const BuyerEngagementsPanel = ({ buyerEngs, onOpen }) => {
  if (buyerEngs != null && buyerEngs.length === 0) return null;  // hide when not a buyer anywhere
  const ndaHue = { signed: 'green', sent: 'amber', redlined: 'amber', declined: 'rose', not_sent: 'slate' };
  const intHue = { high: 'green', medium: 'amber', low: 'slate', passed: 'rose' };
  return (
    <div>
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon.Diamond size={13} color="var(--accent)" />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Buying into</span>
          <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{buyerEngs ? buyerEngs.length : ''}</span>
        </div>
      </div>
      {buyerEngs === null && <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {buyerEngs && buyerEngs.map((b) => (
        <button key={b.id} onClick={() => onOpen(b.engagement_id)} style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px',
          background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
          cursor: 'pointer', textAlign: 'left',
        }}
          onMouseEnter={(ev) => ev.currentTarget.style.background = 'var(--surface-2)'}
          onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.engagement_name}</div>
            {b.seller_company_name && (
              <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Target: {b.seller_company_name}</div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {b.stage_name && <Pill hue="slate" sz="sm">{b.stage_name}</Pill>}
              {b.interest_level && <Pill hue={intHue[b.interest_level] || 'slate'} sz="sm">{b.interest_level}</Pill>}
              {b.nda_status && b.nda_status !== 'not_sent' && <Pill hue={ndaHue[b.nda_status] || 'slate'} sz="sm">NDA {b.nda_status}</Pill>}
              {b.engagement_archived && <Pill hue="slate" sz="sm">archived</Pill>}
            </div>
          </div>
          <Icon.ChevronR size={12} color="var(--text-4)" />
        </button>
      ))}
    </div>
  );
};

// What sold in this company's TRADE near it — the cold caller's opener
// ("your competitor down the road was acquired by …", Zach 2026-09-24).
// Confirmed acquisitions and our own closed deals in the same industry (or
// the seller's own keywords), nearest first; the radius widens 50→100→200
// until a few turn up. Folds like the deal page's sections; hidden when
// nothing in the trade sold nearby or the HQ cannot be placed on the map.
const NearbyAcquisitionsPanel = ({ companyId, sectionId = 'company.nearby_acquisitions' }) => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    setData(null);
    if (!window.AtlasApi.compsNearby) return undefined;
    window.AtlasApi.compsNearby(companyId).then((r) => { if (alive) setData(r || { items: [] }); }).catch(() => { if (alive) setData({ items: [] }); });
    return () => { alive = false; };
  }, [companyId]);
  const items = (data && data.items) || [];
  const Section = window.Section;
  const [sweeping, setSweeping] = React.useState(false);
  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await window.AtlasApi.compsNearbySweep(companyId);
      setData(r || { items: [] });
      const sw = (r && r.swept) || {};
      pushToast({ type: 'success', title: `Web sweep: ${sw.found || 0} sale${sw.found === 1 ? '' : 's'} found in ${sw.industry || 'the trade'} · ${sw.state || ''}` });
    } catch (e) { pushToast({ type: 'error', title: 'Sweep failed', body: String(e.message || e) }); }
    finally { setSweeping(false); }
  };
  if (!Section) return null;
  // Nothing nearby yet: offer the web sweep instead of hiding the panel,
  // but only once the first read is back (data !== null) and the company
  // has a trade to search for.
  if (!items.length) {
    if (data === null || !data.industry || !window.AtlasApi.compsNearbySweep) return null;
    return (
      <Section title="Acquired nearby" collapsible id={sectionId} defaultOpen={false}
        preview={`nothing in ${data.industry} within ${data.radius_mi || 200} mi yet`}
        style={{ border: 'none', borderRadius: 0, borderBottom: '1px solid var(--border)', background: 'transparent' }}>
        <div style={{ padding: '8px 14px 10px', fontSize: 11.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Nothing in {data.industry} sold within {data.radius_mi || 200} miles that Atlas knows of.</span>
          <Btn size="xs" variant="outline" disabled={sweeping} onClick={sweep}>{sweeping ? 'Searching the web…' : 'Search the web'}</Btn>
        </div>
      </Section>
    );
  }
  const first = items[0];
  const preview = `nearest ${first.distance_mi} mi: ${first.company} → ${first.acquirer || 'us'}`;
  const radius = (data && data.radius_mi) || 50;
  return (
    <Section title={`Acquired nearby · ${items.length}`} collapsible id={sectionId} defaultOpen={false}
      preview={preview} style={{ border: 'none', borderRadius: 0, borderBottom: '1px solid var(--border)', background: 'transparent' }}>
      {/* Phone: 10px under the line, so the first row's 44px box stops below Search the web. */}
      <div style={{ padding: rowPhone() ? '6px 14px 10px' : '6px 14px 2px', fontSize: 10.5, color: 'var(--text-4)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>{data.industry || 'Same trade'} sold within {radius} miles</span>
        {window.AtlasApi.compsNearbySweep && (
          <Btn size="xs" variant="ghost" disabled={sweeping} onClick={sweep} title="Search the web for more sales in this trade and state">
            {sweeping ? 'Searching…' : 'Search the web'}
          </Btn>
        )}
      </div>
      {items.map((r) => (
        <div key={`${r.source}-${r.id}`} style={{ padding: '7px 14px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12.5 }}>
            <button onClick={() => window.AtlasNav?.goToCompany?.(r.company_id)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, color: 'var(--text)', textAlign: 'left', fontSize: 12.5, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}
              {r.company}
            </button>
            <span style={{ color: 'var(--text-4)' }}>→</span>
            {r.source === 'ours'
              ? <button onClick={() => r.engagement_id && window.AtlasNav?.goToDeal?.(r.engagement_id)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12.5, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}sold by IndustryPro</button>
              : r.acquirer_company_id
                ? <button onClick={() => window.AtlasNav?.goToCompany?.(r.acquirer_company_id)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12.5, ...(window.phoneHitHost ? window.phoneHitHost() : null) }}>{window.PhoneHit && <window.PhoneHit />}{r.acquirer}</button>
                : <span style={{ color: 'var(--text-2)' }}>{r.acquirer || 'acquired'}</span>}
            <Pill hue={r.distance_mi <= 15 ? 'green' : 'slate'} sz="sm">{r.distance_mi} mi</Pill>
            {r.match === 'keywords' && <Pill hue="slate" sz="sm" title="Matched on the company's own words, not the Atlas industry">related trade</Pill>}
            {r.source === 'web' && <Pill hue="slate" sz="sm" title="Found on the web; not an Atlas company">web</Pill>}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
            {[r.when ? String(r.when).slice(0, 4) : null, r.industry, [r.city, r.state].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
            {r.url && <> · <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>source</a></>}
          </div>
        </div>
      ))}
    </Section>
  );
};

// Sits in the left field panel, between the Company and Lead source groups,
// so it takes the same uppercase group header. Long names/emails ellipsize
// (full text on hover) instead of widening the 360px column.
const RelatedContactsPanel = ({ contacts, onOpen, onCreate }) => (
  <div style={{ paddingBottom: 6 }}>
    <div style={{ padding: '14px 14px 6px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contacts</span>
      {contacts && contacts.length > 0 && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>· {contacts.length}</span>}
      {onCreate && (
        <span style={{ marginLeft: 'auto' }}>
          <Btn size="xs" variant="ghost" icon={<Icon.Plus size={10} />} onClick={onCreate}>Add</Btn>
        </span>
      )}
    </div>
    {contacts === null && <div style={{ padding: '4px 14px 8px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
    {contacts && contacts.length === 0 && (
      <div style={{ padding: '4px 14px 8px', fontSize: 12, color: 'var(--text-3)' }}>No contacts at this company.</div>
    )}
    {contacts && contacts.map((p) => {
      const sub = `${p.title || '—'}${p.email ? ` · ${p.email}` : ''}`;
      const open = () => onOpen && onOpen(p.id);
      // A row (not a <button>) so the call link can sit inside it; the link
      // stops its click, and asks first when the person is do not call.
      // lineHeight 'normal' is what the <button> had, so rows keep their height.
      return (
        <div key={p.id} role={onOpen ? 'button' : undefined} tabIndex={onOpen ? 0 : undefined}
          onClick={open}
          onKeyDown={(ev) => { if (onOpen && ev.target === ev.currentTarget && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); open(); } }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 14px',
            background: 'transparent', border: 'none', boxSizing: 'border-box', lineHeight: 'normal',
            cursor: onOpen ? 'pointer' : 'default', textAlign: 'left',
          }}
          onMouseEnter={(ev) => { if (onOpen) ev.currentTarget.style.background = 'var(--surface-2)'; }}
          onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
        >
          <Avatar name={p.name} size={20} color="oklch(0.50 0.13 295)" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div title={p.name} style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
            <div title={sub} style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
          </div>
          {window.CallButton && (
            <window.CallButton variant="icon" phone={p.phone} doNotCall={!!(p._raw && p._raw.do_not_call)} name={p.name} />
          )}
          {onOpen && <Icon.ChevronR size={12} color="var(--text-4)" />}
        </div>
      );
    })}
  </div>
);

// ============ MyExit Assessment panel =======================================
// Persisted runs of the MyExit Assessment (the vendored owner exit-readiness
// tool at /myexit/index.html — see atlas-crm/myexit/README.md). "Start" creates
// a row server-side and opens the tool in a new tab with a signed link; rows
// reopen the same link to resume an in-progress run or review a completed one.
// "Upload" files the results PDF from a run done outside Atlas (the
// standalone tool, a printed copy) — those rows (source 'upload') have no
// answers/results to reopen, so they open the PDF instead.
const MYEXIT_STATUS_HUE = { in_progress: 'amber', completed: 'green' };
// Display labels for the engine's option keys (chip-length versions of the
// tool's LABELS map — the humanize fallback covers unmapped keys).
const MYEXIT_LABELS = {
  builder: 'Builder', maximizer: 'Maximizer', steward: 'Steward', liberator: 'Liberator',
  entrepreneur: 'Entrepreneur', family_successor: 'Family Successor', portfolio_owner: 'Portfolio Owner',
  act_now: 'Act now', begin_6_12: 'Begin in 6–12 mo', prepare_12_24: 'Prepare 12–24 mo',
  build_24_36: 'Build value 24–36 mo', hold_reassess: 'Hold & reassess',
};
const myexitLabel = (k) =>
  MYEXIT_LABELS[k] || (k || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const MyExitPanel = ({ companyId, contacts }) => {
  const [rows, setRows] = React.useState(null);
  const [starting, setStarting] = React.useState(false);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [toolOpened, setToolOpened] = React.useState(false);   // the tool was opened in a tab from here
  const load = React.useCallback(() => {
    window.AtlasApi.listMyExitAssessments(companyId)
      .then((r) => setRows(Array.isArray(r) ? r : []))
      .catch(() => setRows([]));
  }, [companyId]);
  React.useEffect(() => { setRows(null); setToolOpened(false); load(); }, [load]);
  // The assessment is filled in its own tab — refresh the list when the user
  // comes back to this one, but only while there is something to pick up: a
  // run still in progress, or a run opened from here (a completed one can be
  // adjusted). Every other focus of the window costs nothing.
  const watchFocus = toolOpened || !!(rows && rows.some((r) => r.status === 'in_progress'));
  React.useEffect(() => {
    if (!watchFocus) return undefined;
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [load, watchFocus]);

  const start = async () => {
    if (starting) return;
    // Soft guard against accidental duplicates: an in-progress run is far
    // more likely to be the one they meant to continue than a fresh start.
    const open = (rows || []).find((r) => r.status === 'in_progress');
    if (open) {
      const ok = await showConfirm({
        title: 'Start another assessment?',
        body: `An assessment${open.owner_name ? ` with ${open.owner_name}` : ''} started ${(open.created_at || '').slice(0, 10)} is still in progress — you can resume it from the list instead.`,
        confirmLabel: 'Start new anyway',
      });
      if (!ok) return;
    }
    setStarting(true);
    try {
      // Default the owner to the company's decision maker when there is one.
      const dm = (contacts || []).find((c) => c._raw?.is_decision_maker || c.is_decision_maker);
      const a = await window.AtlasApi.createMyExitAssessment({
        company_id: companyId, contact_id: dm ? dm.id : null,
      });
      load();
      setToolOpened(true);
      window.open(a.tool_url, '_blank');
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not start assessment', body: e.message });
    } finally {
      setStarting(false);
    }
  };

  const openRow = (r) => {
    if (r.source !== 'upload') { setToolOpened(true); window.open(r.tool_url, '_blank'); return; }
    if (!r.pdf_attachment_id) {
      pushToast({ type: 'info', title: 'No PDF on file', body: 'This uploaded assessment has no results PDF attached.' });
      return;
    }
    window.AtlasApi.openAttachment(r.pdf_attachment_id, r.pdf_filename)
      .catch((e) => pushToast({ type: 'error', title: 'Could not open PDF', body: e.message }));
  };

  const remove = async (r) => {
    const done = r.status === 'completed';
    const ok = await showConfirm({
      title: 'Delete this assessment?',
      body: `${r.owner_name || 'Unnamed owner'} · ${((r.completed_at || r.created_at) || '').slice(0, 10)}${done ? ' — its completion note and saved results PDF are deleted too.' : ''}`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await window.AtlasApi.deleteMyExitAssessment(r.id);
      load();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not delete assessment', body: e.message });
    }
  };

  return (
    <div>
      <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon.Target size={13} color="var(--accent)" />
        <span style={{ fontSize: 13, fontWeight: 600 }}>MyExit Assessments</span>
        <span style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--text-4)' }}>{rows ? rows.length || '' : ''}</span>
        <Btn size="xs" variant="ghost" icon={<Icon.Doc size={10} />} onClick={() => setUploadOpen(true)}
          title="File the results PDF from an assessment done outside Atlas">
          Upload
        </Btn>
        <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />} onClick={start} disabled={starting}>
          {starting ? 'Starting…' : 'Start'}
        </Btn>
      </div>
      {uploadOpen && (
        <MyExitUploadDrawer companyId={companyId} contacts={contacts}
          onClose={() => setUploadOpen(false)}
          onDone={() => { setUploadOpen(false); load(); }} />
      )}
      {rows === null && <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {rows && rows.length === 0 && (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
          No assessments yet.
        </div>
      )}
      {rows && rows.map((r) => {
        const s = r.summary || {};
        const done = r.status === 'completed';
        const uploaded = r.source === 'upload';
        // Uploaded runs show the day the assessment happened; tool runs the
        // day they were started (an in-progress one has no completion yet).
        const day = ((uploaded && r.completed_at) || r.created_at || '').slice(0, 10);
        return (
          <button key={r.id} onClick={() => openRow(r)}
            title={uploaded ? 'Open the uploaded results PDF' : done ? 'Open the results (review & adjust)' : 'Resume this assessment'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px',
              background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
              cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={(ev) => ev.currentTarget.style.background = 'var(--surface-2)'}
            onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.owner_name || 'Unnamed owner'}
                <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 400, color: 'var(--text-4)' }}>
                  {day}{r.created_by_name ? ` · ${r.created_by_name}` : ''}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2, alignItems: 'center' }}>
                <Pill hue={MYEXIT_STATUS_HUE[r.status] || 'slate'} sz="sm">{done ? 'Completed' : 'In progress'}</Pill>
                {uploaded && <Pill hue="slate" sz="sm" title="Run outside Atlas — results PDF only">Uploaded PDF</Pill>}
                {done && s.archetype && <Pill hue="violet" sz="sm">{myexitLabel(s.archetype)}</Pill>}
                {done && s.timing && <Pill hue="teal" sz="sm">{myexitLabel(s.timing)}</Pill>}
                {done && s.confidence != null && (
                  <span style={{ fontSize: 10.5, color: 'var(--text-4)' }} title={s.confidence_band || ''}>
                    {s.confidence}% confidence
                  </span>
                )}
              </div>
            </div>
            {/* Phone: the ✕ is its own 44px square at the row's end (no overflow box), so a
                tap on the row's text still opens the assessment, never the delete confirm. */}
            <span role="button" title="Delete this assessment"
              onClick={(ev) => { ev.stopPropagation(); remove(r); }}
              style={{ fontSize: 13, lineHeight: 1, padding: '2px 5px', borderRadius: 4, color: 'var(--text-4)',
                ...(rowPhone() ? { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, padding: 0, margin: '-10px -6px', flexShrink: 0 } : null) }}
              onMouseEnter={(ev) => { ev.currentTarget.style.color = 'var(--danger, #c0392b)'; ev.currentTarget.style.background = 'var(--surface-3, var(--surface-2))'; }}
              onMouseLeave={(ev) => { ev.currentTarget.style.color = 'var(--text-4)'; ev.currentTarget.style.background = 'transparent'; }}
            >✕</span>
            {uploaded ? <Icon.Doc size={12} color="var(--text-4)" /> : <Icon.External size={12} color="var(--text-4)" />}
          </button>
        );
      })}
    </div>
  );
};

// Drawer for filing an assessment done outside Atlas: pick the owner (a
// contact at the company, or a typed name), the day it happened, and the
// results PDF. POST /myexit/upload records it as a completed run.
const MYEXIT_OTHER_OWNER = '__other__';
// Local calendar day as YYYY-MM-DD — toISOString() is UTC and reads as
// tomorrow every US evening.
const localDay = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// `zIndex` (optional) stacks it over an open reference drawer (the v2 page's ⋯ › Upload MyExit…).
const MyExitUploadDrawer = ({ companyId, contacts, onClose, onDone, zIndex }) => {
  const dm = (contacts || []).find((c) => c._raw?.is_decision_maker || c.is_decision_maker);
  const [contactId, setContactId] = React.useState(dm ? dm.id : ((contacts || []).length ? '' : MYEXIT_OTHER_OWNER));
  const [ownerName, setOwnerName] = React.useState('');
  const [completedOn, setCompletedOn] = React.useState(localDay());
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);

  const pick = (list) => {
    const f = list && list[0];
    if (!f) return;
    const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');
    if (!isPdf) { pushToast({ type: 'error', title: 'PDF only', body: 'Upload the results PDF the assessment produced.' }); return; }
    setFile(f);
  };

  const ownerOptions = [
    ...(contacts || []).map((c) => ({ value: c.id, label: c.name, description: c.title || undefined })),
    { value: MYEXIT_OTHER_OWNER, label: 'Someone else…' },
  ];
  const otherOwner = contactId === MYEXIT_OTHER_OWNER;
  const canSubmit = !!file && !busy && (!otherOwner || ownerName.trim());

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('company_id', companyId);
      if (contactId && !otherOwner) fd.append('contact_id', contactId);
      if (otherOwner && ownerName.trim()) fd.append('owner_name', ownerName.trim());
      if (completedOn) fd.append('completed_on', completedOn);
      fd.append('file', file, file.name);
      await window.AtlasApi.uploadMyExitAssessment(fd);
      pushToast({ type: 'success', title: 'Assessment filed', body: `${file.name} is on the company's Files panel.` });
      onDone();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not upload assessment', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <window.Drawer open onClose={onClose} title="Upload a MyExit Assessment" width={400} zIndex={zIndex}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn size="sm" variant="primary" onClick={submit} disabled={!canSubmit}>{busy ? 'Uploading…' : 'Upload'}</Btn>
        </div>
      }>
      <Field label="Owner">
        <Combobox value={contactId} onChange={(e) => setContactId(e.target.value)} options={ownerOptions}
          placeholder="Who took the assessment?" searchable={(contacts || []).length > 6} />
      </Field>
      {otherOwner && (
        <Field label="Owner name">
          <TextInput value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Full name" autoFocus />
        </Field>
      )}
      <Field label="Date completed" hint="The day the owner went through it — the completion note lands on that day.">
        <TextInput type="date" value={completedOn} max={localDay()}
          onChange={(e) => setCompletedOn(e.target.value)} />
      </Field>
      <Field label="Results PDF" hint="The PDF the tool produced (or a scan of a printed copy). Answers aren't captured — only the PDF is kept.">
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
          onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
        <div role="button" tabIndex={0}
          onClick={() => inputRef.current && inputRef.current.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer?.files); }}
          style={{
            padding: '16px 12px', textAlign: 'center', fontSize: 12.5, cursor: 'pointer', borderRadius: 6,
            border: `1px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
            background: dragOver ? 'var(--surface-2)' : 'var(--surface)', color: file ? 'var(--text)' : 'var(--text-3)',
          }}>
          {file ? (
            <>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>{(file.size / 1024 / 1024).toFixed(1)} MB · click to swap</div>
            </>
          ) : 'Drop the PDF here or click to choose'}
        </div>
      </Field>
    </window.Drawer>
  );
};

// ============ Stage signals widgets =========================================
// Surfaces the evidence the signal pre-cleaner mined from notes + DC date
// fields + engagements (see scripts/compute_company_signals.py). Read-only
// for now — the auto-promote rule is the only writer.
const SIGNAL_ORDER = ['intro_or_ds_call','pb_blast','peg_blast','bs_call','nda_signed','loi','closing','won'];
const SIGNAL_LABEL = {
  intro_or_ds_call: 'D/S call',
  pb_blast:         'PB Blast',
  peg_blast:        'PEG Blast',
  bs_call:          'B/S call',
  nda_signed:       'NDA signed',
  loi:              'LOI',
  closing:          'Closing',
  won:              'Won',
};
const SIGNAL_HUE = {
  intro_or_ds_call: 'teal',
  pb_blast:         'violet',
  peg_blast:        'blue',
  bs_call:          'amber',
  nda_signed:       'slate',
  loi:              'amber',
  closing:          'green',
  won:              'green',
};

const TrackerStageCell = ({ company }) => {
  const stage = company.trackerStage;
  const ap = (company.signals || {}).auto_promoted;
  if (!stage) return <span style={{ color: 'var(--text-4)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Pill hue={stage === 'Closed' || stage === 'Won' ? 'green' : 'slate'} sz="sm">{stage}</Pill>
      {ap && ap.to === stage && (
        <span title={`Auto-promoted from "${ap.from ?? '(none)'}" on ${ap.at?.slice(0,10)}\nReason: ${ap.reason}\nEvidence: ${(ap.evidence||[]).join(', ')}`}
          style={{ fontSize: 10.5, color: 'var(--text-4)', fontStyle: 'italic', cursor: 'help' }}>
          auto-promoted
        </span>
      )}
    </span>
  );
};

const SignalsChips = ({ signals }) => {
  // Filter to reached + sort by canonical funnel order so the visual story
  // matches the actual progression.
  const reached = SIGNAL_ORDER.filter((k) => signals[k]?.reached);
  if (reached.length === 0) {
    return <span style={{ fontSize: 11, color: 'var(--text-4)' }}>No milestones yet.</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {reached.map((k) => {
        const v = signals[k];
        const tooltip = [
          `Evidence: ${v.count ?? 1} record${(v.count ?? 1) === 1 ? '' : 's'}`,
          v.first_seen ? `First seen: ${v.first_seen}` : null,
          (v.evidence || []).slice(0, 3).map((e) => `  • ${e}`).join('\n'),
        ].filter(Boolean).join('\n');
        // Resolve through the HUE token map — 'blue' (peg_blast) has no
        // --blue-* css vars, it aliases to the accent color.
        const h = HUE[SIGNAL_HUE[k]] || HUE.slate;
        return (
          <span key={k} title={tooltip}
            style={{
              fontSize: 10.5, padding: '2px 7px', borderRadius: 4,
              display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help',
              background: h.bg, color: h.fg,
              fontWeight: 500,
            }}>
            ✓ {SIGNAL_LABEL[k]}
            {v.first_seen && (
              <span style={{ opacity: 0.55, fontVariantNumeric: 'tabular-nums', fontSize: 10 }}>
                {v.first_seen.slice(0, 4)}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
};

window.CompanyDetail        = CompanyDetail;
window.acquisitionToast     = acquisitionToast;
// Shared note helpers — reused by RecordNotes (views/record_notes.jsx) on every record page.
window.NOTE_TYPE_LABELS     = NOTE_TYPE_LABELS;
window.NoteFilterChips      = NoteFilterChips;
window.filterNotes          = filterNotes;
window.groupEmailThreads    = groupEmailThreads;
window.EmailThreadCard      = EmailThreadCard;
window.groupMeetingNotes    = groupMeetingNotes;
window.MeetingGroupCard     = MeetingGroupCard;
window.noteFacets           = noteFacets;
window.usePagedNotes        = usePagedNotes;
window.NotesLoadMoreRow     = NotesLoadMoreRow;
window.useNoteAiPreviews    = useNoteAiPreviews;
window.NotePreviewBody      = NotePreviewBody;
window.NoteSearchInput      = NoteSearchInput;
window.highlightText        = highlightText;
window.RelatedEngagementsPanel = RelatedEngagementsPanel;
window.DKpi                 = DKpi;
window.TrackerStageCell     = TrackerStageCell;
window.SignalsChips         = SignalsChips;
window.SourceScrubPanel     = SourceScrubPanel;
// Formatting helpers the phone Companies list (src/mobile/companies.jsx) shares.
window.CompanyPhoneKit      = { phoneDial, cityStateShort, shortAgo };
// Wave D (step 11): blocks the v2 company page (views/company_page_v2.jsx)
// reuses verbatim. Read at render time there.
window.CompanyPropertiesPanel = CompanyPropertiesPanel;
window.OwnershipPanel       = OwnershipPanel;
window.OwnershipStatus      = OwnershipStatus;
window.MyExitPanel          = MyExitPanel;
window.MyExitUploadDrawer   = MyExitUploadDrawer;
window.EngagementRow        = EngagementRow;
window.CompanyTasksCard     = CompanyTasksCard;
window.myexitLabel          = myexitLabel;
